import { getAppMode, getSquareConfig } from "../config";
import {
  deleteCurrentUser,
  getAuthSnapshot,
  getFirebaseIdToken,
  onAuthStateChange,
  signInWithGoogle,
  signInWithMagicLink,
  signOut,
  type AuthSnapshot,
} from "../lib/auth";
import { parsePhoneListText, parseUploadFile } from "../lib/csv";
import { usagePlans } from "../lib/defaults";
import {
  addAuthorizedUser,
  addRowsToBatch,
  addTestingAccount,
  removeTestingAccount,
  checkIsTestingAccount,
  clearBatchData,
  clearPhoneNumberBlockers,
  closeUpload,
  createProfile,
  deleteAccountData,
  refillTokens,
  loadAppData,
  loadAuthorizedUsers,
  loadLinkedAccounts,
  loadNumberHistory,
  loadPhoneLog,
  loadTestingAccounts,
  markUploadReady,
  removeAuthorizedUser,
  removeRowFromBatch,
  saveBusinessProfile,
  saveBillingSetup,
  saveSubscriptionCreated,
  saveSubscriptionCanceled,
  saveManualBatch,
  saveValidatedUpload,
  loadUploadDetails,
} from "../lib/data";
import type {
  AccountAccess,
  AppData,
  BusinessAccount,
  BusinessProfile,
  CsvValidationResult,
  NumberHistoryEntry,
  PhoneLogEntry,
  ProfileAnalytics,
  ReviewPlatform,
  TestingAccount,
  UploadDetails,
  UploadRecord,
  UploadRow,
  UsagePlanId,
} from "../lib/types";

type RouteName =
  | "dashboard"
  | "profile"
  | "profile-select"
  | "upload"
  | "billing"
  | "log"
  | "numbers"
  | "team"
  | "test-accounts"
  | "admin";

// Issue #53: a small, fixed allowlist (not an extensible Firestore-backed
// list like testing_accounts) — real enforcement is server-side in
// /v1/admin/accounts; this is UX-only (nav visibility).
const ADMIN_EMAILS = ["bennettrsimpson@gmail.com", "bennett@huple.net", "thomaslsimpson@gmail.com"];
function isAdminEmail(email: string | null | undefined): boolean {
  return !!email && ADMIN_EMAILS.includes(email.toLowerCase());
}

type AdminAccountProfile = {
  profile_id: string;
  business_name: string;
  contact_name: string;
  primary_email: string;
};
type AdminAccountOverview = {
  account_id: string;
  email: string;
  billing_status: string;
  usage_plan_id: string;
  token_balance: number;
  square_customer_id: string;
  square_subscription_id: string;
  next_billing_date: string;
  created_at: string;
  profiles: AdminAccountProfile[];
  real_plan_id: string;
  plan_mismatch: boolean;
  operates_under_email: string;
};

type AdminTab = "accounts" | "activity-log" | "phone-numbers" | "opt-outs" | "transactions" | "customer-stats";

type AdminActivityLogEntry = {
  timestamp: string;
  account_id: string;
  account_email: string;
  profile_id: string;
  business_name: string;
  phone: string;
  event: string;
  detail: string;
};

type AdminPhoneNumberEntry = {
  account_id: string;
  account_email: string;
  profile_id: string;
  business_name: string;
  phone_number: string;
  state: string;
  last_contacted_at: string;
  updated_at: string;
};

type AdminOptOutEntry = {
  phone_number: string;
  opted_out_at: string;
  last_stop_at: string;
  expires_at: string;
  stop_count: number;
  source_account_id: string;
  source_profile_id: string;
};

type AdminTransactionEntry = {
  payment_id: string;
  created_at: string;
  status: string;
  amount_cents: number;
  currency: string;
  customer_id: string;
  account_id: string;
  email: string;
};

type AdminSubscriptionEntry = {
  subscription_id: string;
  customer_id: string;
  plan_id: string;
  status: string;
  created_at: string;
  charged_through_date: string;
  canceled_date: string;
  account_id: string;
  email: string;
  stored_plan_id: string;
  plan_mismatch: boolean;
  stored_subscription_id: string;
  missing_from_account: boolean;
};

type AdminTransactionsData = {
  payments: AdminTransactionEntry[];
  subscriptions: AdminSubscriptionEntry[];
};

// Issue #68: per-customer usage/effectiveness stats, scoped to a trailing
// window and summed across every profile under the selected account.
type AdminCustomerStatsRange = "week" | "month" | "quarter";

type AdminCustomerStatsProfile = {
  profile_id: string;
  business_name: string;
  messages_sent: number;
  unique_numbers_contacted: number;
  review_clicks: number;
};

type AdminCustomerStatsData = {
  account_id: string;
  range: AdminCustomerStatsRange;
  since: string;
  messages_sent: number;
  unique_numbers_contacted: number;
  review_clicks: number;
  profiles: AdminCustomerStatsProfile[];
};

const routes: Array<{ name: RouteName; label: string; path: string }> = [
  { name: "dashboard", label: "Home", path: "/" },
  { name: "profile", label: "Profile", path: "/profile" },
  { name: "upload", label: "Upload", path: "/upload" },
  { name: "billing", label: "Billing", path: "/billing" },
  { name: "log", label: "Activity Log", path: "/log" },
  { name: "numbers", label: "Number History", path: "/numbers" },
  { name: "team", label: "Team", path: "/team" },
];

// Minimal Square Web Payments SDK types
interface SquareCard {
  attach(selector: string): Promise<void>;
  tokenize(): Promise<{ status: string; token?: string; errors?: Array<{ message: string }> }>;
  destroy(): Promise<void>;
}
interface SquarePayments {
  card(): Promise<SquareCard>;
}
declare global {
  interface Window { Square?: { payments(appId: string, locationId: string): Promise<SquarePayments> } }
}

// Lives outside state so it survives render() calls on non-billing pages,
// but is nulled out before every render to avoid stale iframe references.
let squareCard: SquareCard | null = null;
let squareInitCancelled = false;

const defaultReviewPlatforms: ReviewPlatform[] = [
  { id: "google", name: "Google", url: "", enabled: true },
  { id: "facebook", name: "Facebook", url: "", enabled: true },
];

const guestAccountStorageKey = "ratefollowup.guestAccountId";
const guestOwnerEmailStorageKey = "ratefollowup.guestOwnerEmail";

export function createApp(root: HTMLDivElement | null) {
  if (!root) {
    throw new Error("Application root not found.");
  }

  const mountNode = root;

  // Tracks which public (/r/, /f/) pathname has already been initialized, so
  // repeated render() calls on the same load don't re-fire the click-tracking
  // fetch. See render()'s public-route branch below.
  let lastPublicPagePath: string | null = null;

  const state = {
    auth: {
      configured: false,
      session: null,
      user: null,
    } as AuthSnapshot,
    appData: null as AppData | null,
    route: resolveRoute(window.location.pathname),
    busy: false,
    loadingData: false,
    notice: "",
    error: "",
    csvResult: null as CsvValidationResult | null,
    csvFileName: "",
    lastSavedUploadId: "",
    selectedUploadDetails: null as UploadDetails | null,
    uploadAttested: false,
    billingShowUpdateForm: false,
    billingSelectedPlanId: null as string | null,
    billingConfirmChecked: false,
    billingDemoConfirmChecked: false,
    billingModalOpen: null as "plan" | "demo" | "cancel" | null,
    uploadMode: "file" as "file" | "manual",
    sendStatus: "idle" as "idle" | "sending" | "complete" | "failed" | "stopped",
    sendResult: null as SendResult | null,
    sendAbortController: null as AbortController | null,
    sendPollTimer: null as ReturnType<typeof setInterval> | null,
    sendPollTicks: 0,
    billingPollTimer: null as ReturnType<typeof setInterval> | null,
    billingPollTicks: 0,
    selectedProfileId: sessionStorage.getItem("selectedProfileId") as string | null,
    phoneLog: null as PhoneLogEntry[] | null,
    phoneLogLoading: false,
    logPhoneFilter: "" as string, // digits-only phone key; "" = all numbers
    pendingLogFilter: "" as string, // set by "view in log" drill-down, applied on next log load
    numberHistory: null as NumberHistoryEntry[] | null,
    numberHistoryLoading: false,
    numberHistoryFilter: "" as string, // search box text
    isTesting: false,
    activeAccountIsTesting: false,
    testingAccounts: null as TestingAccount[] | null,
    testingAccountsLoading: false,
    adminTab: "accounts" as AdminTab,
    adminAccounts: null as AdminAccountOverview[] | null,
    adminAccountsLoading: false,
    adminActivityLog: null as AdminActivityLogEntry[] | null,
    adminActivityLogLoading: false,
    adminActivityLogFilter: "",
    adminPhoneNumbers: null as AdminPhoneNumberEntry[] | null,
    adminPhoneNumbersLoading: false,
    adminPhoneNumbersFilter: "",
    adminOptOuts: null as AdminOptOutEntry[] | null,
    adminOptOutsLoading: false,
    adminTransactions: null as AdminTransactionsData | null,
    adminTransactionsLoading: false,
    adminCustomerStatsAccountId: null as string | null,
    adminCustomerStatsRange: "week" as AdminCustomerStatsRange,
    adminCustomerStats: null as AdminCustomerStatsData | null,
    adminCustomerStatsLoading: false,
    authorizedUsers: null as AccountAccess[] | null,
    authorizedUsersLoading: false,
    linkedAccounts: null as AccountAccess[] | null,
    guestAccountId: localStorage.getItem(guestAccountStorageKey) as string | null,
    guestOwnerEmail: localStorage.getItem(guestOwnerEmailStorageKey) as string | null,
    showClosedBatches: false,
    dashboardBatchId: "all" as string,
  };

  let pendingPlatforms: ReviewPlatform[] = [...defaultReviewPlatforms];

  function setNotice(message = "") {
    state.notice = message;
  }

  function setError(message = "") {
    state.error = message;
  }

  function setBusy(value: boolean) {
    state.busy = value;
  }

  function navigate(path: string) {
    console.log(`[nav] ${window.location.pathname} → ${path}`);
    stopSendPoll();
    window.history.pushState({}, "", path);
    state.route = resolveRoute(path);
    render();
    if (path === "/log") {
      loadActivityLog();
    }
    if (path === "/numbers") {
      loadNumberHistoryPage();
    }
    if (path === "/test-accounts") {
      handleLoadTestingAccounts();
    }
    if (path === "/admin") {
      handleLoadAdminAccounts();
    }
    if (path === "/team") {
      handleLoadAuthorizedUsers();
    }
  }

  async function loadActivityLog() {
    const account = state.appData?.account;
    const profile = state.appData?.profile;
    if (!account || !profile) return;
    // A drill-down from Number History sets pendingLogFilter; otherwise the log
    // opens showing all numbers.
    const applyFilter = state.pendingLogFilter;
    state.pendingLogFilter = "";
    state.phoneLogLoading = true;
    state.logPhoneFilter = applyFilter;
    render();
    try {
      state.phoneLog = await loadPhoneLog(account.id, profile.id);
      console.log(`[log] loaded ${state.phoneLog.length} entries`);
    } catch (error) {
      console.error("[log] load failed:", error);
    } finally {
      state.phoneLogLoading = false;
      render();
    }
  }

  async function loadNumberHistoryPage() {
    const account = state.appData?.account;
    const profile = state.appData?.profile;
    if (!account || !profile) return;
    state.numberHistoryLoading = true;
    state.numberHistoryFilter = "";
    render();
    try {
      state.numberHistory = await loadNumberHistory(account.id, profile.id);
      console.log(`[numbers] loaded ${state.numberHistory.length} numbers`);
    } catch (error) {
      console.error("[numbers] load failed:", error);
    } finally {
      state.numberHistoryLoading = false;
      render();
    }
  }

  function handleViewNumberInLog(digits: string) {
    state.pendingLogFilter = digits;
    navigate("/log");
  }

  window.addEventListener("popstate", () => {
    state.route = resolveRoute(window.location.pathname);
    render();
    if (state.route === "test-accounts") {
      handleLoadTestingAccounts();
    }
    if (state.route === "admin") {
      handleLoadAdminAccounts();
    }
    if (state.route === "team") {
      handleLoadAuthorizedUsers();
    }
  });

  async function refreshAppData() {
    if (!state.auth.user) {
      state.appData = null;
      state.isTesting = false;
      state.activeAccountIsTesting = false;
      return;
    }

    console.log(`[data] refreshAppData user=${state.auth.user.email} profileId=${state.selectedProfileId ?? "none"}`);
    state.loadingData = true;
    render();

    try {
      const accountIdToLoad = state.guestAccountId ?? state.auth.user.id;
      const [data, isTesting, linkedAccounts] = await Promise.all([
        loadAppData(
          accountIdToLoad,
          state.auth.user.email ?? null,
          state.selectedProfileId ?? undefined,
        ),
        checkIsTestingAccount(state.auth.user.email ?? ""),
        state.guestAccountId ? Promise.resolve(state.linkedAccounts ?? []) : loadLinkedAccounts(state.auth.user.email ?? ""),
      ]);
      let effectiveData = data;
      state.isTesting = isTesting;
      state.activeAccountIsTesting = isTesting;
      if (!state.guestAccountId) state.linkedAccounts = linkedAccounts;
      if (!state.guestAccountId && linkedAccounts.length === 1 && data.profiles.length === 0) {
        const access = linkedAccounts[0];
        state.guestAccountId = access.owner_account_id;
        state.guestOwnerEmail = access.owner_email;
        localStorage.setItem(guestAccountStorageKey, access.owner_account_id);
        localStorage.setItem(guestOwnerEmailStorageKey, access.owner_email);
        state.selectedProfileId = null;
        sessionStorage.removeItem("selectedProfileId");
        effectiveData = await loadAppData(
          access.owner_account_id,
          state.auth.user.email ?? null,
          undefined,
        );
        state.activeAccountIsTesting = await checkIsTestingAccount(access.owner_email);
      } else if (state.guestAccountId && state.guestOwnerEmail) {
        state.activeAccountIsTesting = await checkIsTestingAccount(state.guestOwnerEmail);
      }
      state.appData = effectiveData;
      const dataForRouting = effectiveData;
      console.log(`[data] loaded: ${dataForRouting.profiles.length} profile(s), selected=${dataForRouting.profile?.id ?? "none"}, uploads=${dataForRouting.uploads.length}, complete=${dataForRouting.profile?.is_profile_complete ?? false}`);

      if (!state.selectedProfileId) {
        if (dataForRouting.profiles.length === 0) {
          // New user — send to home for onboarding
          console.log("[data] no profiles → /");
          navigate("/");
        } else {
          // Has profiles — auto-select if exactly 1, then always send to locations page
          if (dataForRouting.profiles.length === 1) {
            state.selectedProfileId = dataForRouting.profiles[0].id;
            sessionStorage.setItem("selectedProfileId", dataForRouting.profiles[0].id);
            console.log(`[data] auto-selected only profile: ${dataForRouting.profiles[0].id}`);
            state.appData = await loadAppData(
              state.guestAccountId ?? state.auth.user.id,
              state.auth.user.email ?? null,
              state.selectedProfileId,
            );
          }
          console.log(`[data] has profiles → /profile-select`);
          navigate("/profile-select");
        }
      }
      if (state.isTesting && state.route === "test-accounts" && state.testingAccounts === null) {
        handleLoadTestingAccounts();
      }
      if (isAdminEmail(state.auth.user?.email) && state.route === "admin" && state.adminAccounts === null) {
        handleLoadAdminAccounts();
      }
    } catch (error) {
      console.error("[data] refreshAppData failed:", error);
      setError(readError(error));
    } finally {
      state.loadingData = false;
      render();
    }
  }

  async function handleGoogleSignIn() {
    setBusy(true);
    setError();
    setNotice();
    render();

    try {
      await signInWithGoogle();
    } catch (error) {
      setError(readError(error));
      setBusy(false);
      render();
    }
  }

  async function handleMagicLink(event: SubmitEvent) {
    event.preventDefault();
    setBusy(true);
    setError();
    setNotice();
    render();

    const form = event.currentTarget as HTMLFormElement;
    const formData = new FormData(form);
    const email = String(formData.get("email") ?? "").trim();

    if (!email) {
      setError("Enter an email address.");
      setBusy(false);
      render();
      return;
    }

    try {
      await signInWithMagicLink(email);
      form.reset();
      setNotice("Magic link sent. Check your email to finish signing in.");
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleSignOut() {
    console.log("[auth] sign out requested");
    setBusy(true);
    setError();
    setNotice();
    render();

    try {
      await signOut();
      state.appData = null;
      state.csvResult = null;
      state.csvFileName = "";
      state.lastSavedUploadId = "";
      state.selectedUploadDetails = null;
      state.guestAccountId = null;
      state.guestOwnerEmail = null;
      localStorage.removeItem(guestAccountStorageKey);
      localStorage.removeItem(guestOwnerEmailStorageKey);
      navigate("/");
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleProfileSave(event: Event) {
    const form = mountNode.querySelector<HTMLFormElement>("[data-profile-form]");
    if (!form) return;
    const formData = new FormData(form);
    const platformsSnapshot = pendingPlatforms.map((p) => ({ ...p }));

    // Disable the button in-place without a full render so the form values
    // stay visible and pendingPlatforms is not reset mid-save.
    const submitBtn = event.currentTarget as HTMLButtonElement;
    submitBtn.disabled = true;
    submitBtn.textContent = "Saving…";

    setError();
    setNotice();

    try {
      const account = state.appData?.account;
      if (!account) throw new Error("Account not found. Please sign out and sign back in.");

      let profile = state.appData?.profile ?? null;
      if (!profile) {
        // Profile was deleted or never created — create one on the fly.
        const locationName = String(formData.get("location_name") ?? "").trim() || "My Business";
        profile = await createProfile(account.id, locationName);
        if (state.appData) {
          state.appData.profiles = [...(state.appData.profiles ?? []), profile];
          state.appData.profile = profile;
        }
        state.selectedProfileId = profile.id;
        sessionStorage.setItem("selectedProfileId", profile.id);
      }

      const updated = await saveBusinessProfile(account.id, profile.id, {
        location_name: String(formData.get("location_name") ?? "").trim() || profile.location_name,
        business_name: String(formData.get("business_name") ?? "").trim().slice(0, 30),
        business_address: String(formData.get("business_address") ?? "").trim(),
        business_phone: String(formData.get("business_phone") ?? "").trim(),
        contact_name: String(formData.get("contact_name") ?? "").trim(),
        primary_email: String(formData.get("primary_email") ?? "").trim(),
        timezone: String(formData.get("timezone") ?? "America/Chicago"),
        report_preference: (String(formData.get("report_preference") ?? "daily_activity") as "daily_always" | "daily_activity" | "weekly" | "none"),
        review_platforms: platformsSnapshot,
      });

      if (state.appData) state.appData.profile = updated;
      setNotice("Profile saved.");

      // Railroad users through the site (issue #63): always send them back
      // to the dashboard to see the next step, rather than leaving them on
      // the form where they could get distracted or wander off-path.
      navigate("/");
    } catch (error) {
      setError(readError(error));
      render();
      const failedBtn = mountNode.querySelector<HTMLButtonElement>("[data-save-profile]");
      if (failedBtn) {
        failedBtn.textContent = "Save failed";
        failedBtn.classList.add("profile-save-failed");
        setTimeout(() => {
          failedBtn.textContent = "Save profile";
          failedBtn.classList.remove("profile-save-failed");
        }, 3000);
      }
    }
  }

  async function handleFileSelected(event: Event) {
    const input = event.currentTarget as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    setBusy(true);
    setError();
    setNotice();
    state.csvResult = null;
    state.csvFileName = file.name;
    render();

    try {
      if (!state.appData?.account || !state.appData.profile) {
        throw new Error("Business profile is not available.");
      }

      const attestedBy = state.auth.user?.email ?? "";
      const validation = await parseUploadFile(file, attestedBy, state.activeAccountIsTesting);
      state.csvResult = validation;

      const batchNameInput = mountNode.querySelector<HTMLInputElement>("[data-batch-name]");
      const batchName = batchNameInput?.value.trim() || undefined;
      const savedUpload = await saveValidatedUpload(
        state.appData.account.id,
        state.appData.profile.id,
        state.auth.user?.id ?? "",
        file,
        validation,
        batchName,
      );

      state.lastSavedUploadId = savedUpload.upload.id;
      state.selectedUploadDetails = savedUpload;
      await refreshAppData();
      setNotice(`${validation.summary.validRows} numbers ready, ${validation.summary.rejectedRows} rejected.`);
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleSelectUpload(event: Event) {
    const button = event.currentTarget as HTMLButtonElement;
    const uploadId = button.dataset.uploadId;
    const account = state.appData?.account;
    const profile = state.appData?.profile;
    if (!account || !profile || !uploadId) return;

    setBusy(true);
    setError();
    setNotice();
    render();

    try {
      state.selectedUploadDetails = await loadUploadDetails(account.id, profile.id, uploadId);
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleDemoCharge() {
    const account = state.appData?.account;
    if (!account) return;

    const btn = mountNode.querySelector<HTMLButtonElement>("[data-demo-confirm]");
    const errorEl = mountNode.querySelector<HTMLElement>("#demo-charge-error");
    if (btn) { btn.disabled = true; btn.textContent = "Processing…"; }
    if (errorEl) { errorEl.style.display = "none"; errorEl.textContent = ""; }

    try {
      const idToken = await getFirebaseIdToken();
      if (!idToken) throw new Error("Authentication required.");
      const { apiBaseUrl } = getSquareConfig();

      const chargeResponse = await fetch(`${apiBaseUrl}/v1/billing/charge`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
        body: JSON.stringify({
          account_id: account.id,
          customer_id: account.square_customer_id,
          card_id: account.square_card_id,
          plan_id: "demo_12",
        }),
      });

      if (!chargeResponse.ok) {
        const d = await chargeResponse.json().catch(() => ({})) as { error?: string };
        throw new Error(d.error ?? `Charge failed (${chargeResponse.status})`);
      }

      if (state.appData) state.appData.account = await refillTokens(account.id, 12);
      state.billingModalOpen = null;
      state.billingDemoConfirmChecked = false;
      setNotice("Your purchase was successful! Tokens have been awarded.");
      render();
    } catch (error) {
      if (btn) { btn.disabled = false; btn.textContent = "Confirm Purchase"; }
      if (errorEl) {
        errorEl.textContent = error instanceof Error ? error.message : "An unexpected error occurred.";
        errorEl.style.display = "";
      }
    }
  }

  async function handleBillingSubmit() {
    const account = state.appData?.account;
    if (!account || !squareCard) return;

    const btn = mountNode.querySelector<HTMLButtonElement>("[data-billing-submit]");
    const errorEl = mountNode.querySelector<HTMLElement>("#billing-error");
    if (btn) { btn.disabled = true; btn.textContent = "Processing…"; }
    if (errorEl) { errorEl.style.display = "none"; errorEl.textContent = ""; }

    try {
      const result = await squareCard.tokenize();
      if (result.status !== "OK" || !result.token) {
        throw new Error(result.errors?.[0]?.message ?? "Card verification failed. Check your details.");
      }

      const idToken = await getFirebaseIdToken();
      if (!idToken) throw new Error("Authentication required.");

      const { apiBaseUrl } = getSquareConfig();
      const response = await fetch(`${apiBaseUrl}/v1/billing/setup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          source_id: result.token,
          account_id: account.id,
          email: state.auth.user?.email ?? "",
          existing_customer_id: account.square_customer_id ?? "",
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({})) as { error?: string };
        throw new Error(data.error ?? `Server error (${response.status})`);
      }

      const data = await response.json() as {
        customer_id: string;
        card_id: string;
        card_last4: string;
        card_brand: string;
      };

      if (state.appData) state.appData.account = await saveBillingSetup(account.id, {
        square_customer_id: data.customer_id,
        square_card_id: data.card_id,
        card_last4: data.card_last4,
        card_brand: data.card_brand,
      });

      state.billingShowUpdateForm = false;
      state.billingConfirmChecked = false;
      setNotice("Payment method saved. Select a plan below and confirm to activate.");
      render();
    } catch (error) {
      if (btn) { btn.disabled = false; btn.textContent = "Save payment method"; }
      if (errorEl) {
        errorEl.textContent = error instanceof Error ? error.message : "An unexpected error occurred.";
        errorEl.style.display = "";
      }
    }
  }

  async function handleConfirmPurchase() {
    const account = state.appData?.account;
    if (!account || !state.billingSelectedPlanId) return;

    const btn = mountNode.querySelector<HTMLButtonElement>("[data-confirm-purchase]");
    const errorEl = mountNode.querySelector<HTMLElement>("#confirm-purchase-error");
    if (btn) { btn.disabled = true; btn.textContent = "Processing…"; }
    if (errorEl) { errorEl.style.display = "none"; errorEl.textContent = ""; }

    try {
      const idToken = await getFirebaseIdToken();
      if (!idToken) throw new Error("Authentication required.");
      const { apiBaseUrl } = getSquareConfig();

      const subscribeResponse = await fetch(`${apiBaseUrl}/v1/billing/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
        body: JSON.stringify({
          account_id: account.id,
          customer_id: account.square_customer_id,
          card_id: account.square_card_id,
          plan_id: state.billingSelectedPlanId,
        }),
      });

      if (!subscribeResponse.ok) {
        const d = await subscribeResponse.json().catch(() => ({})) as { error?: string };
        throw new Error(d.error ?? `Subscription failed (${subscribeResponse.status})`);
      }

      const { subscription_id } = await subscribeResponse.json() as { subscription_id: string };

      if (state.appData) state.appData.account = await saveSubscriptionCreated(account.id, subscription_id);

      state.billingConfirmChecked = false;
      state.billingSelectedPlanId = null;
      state.billingModalOpen = null;
      // Tokens aren't actually credited yet at this point — Square's
      // invoice.payment_made webhook does that asynchronously, sometimes a
      // few seconds after this call returns (see saveSubscriptionCreated in
      // src/lib/data.ts). Say so rather than claiming it's already done, and
      // poll until it lands so the balance updates on its own (issue #65).
      setNotice("Your purchase was successful! Your subscription is being set up — tokens will appear shortly.");
      startBillingPoll();

      // Railroad users through the site (issue #63): always send them back
      // to the dashboard to see what's left before they can send messages,
      // rather than leaving them on the billing page.
      navigate("/");
    } catch (error) {
      if (btn) { btn.disabled = false; btn.textContent = "Confirm Purchase"; }
      if (errorEl) {
        errorEl.textContent = error instanceof Error ? error.message : "An unexpected error occurred.";
        errorEl.style.display = "";
      }
    }
  }

  async function handleCancelSubscription() {
    const account = state.appData?.account;
    if (!account) return;

    const btn = mountNode.querySelector<HTMLButtonElement>("[data-cancel-confirm]");
    const errorEl = mountNode.querySelector<HTMLElement>("#cancel-subscription-error");
    if (btn) { btn.disabled = true; btn.textContent = "Processing…"; }
    if (errorEl) { errorEl.style.display = "none"; errorEl.textContent = ""; }

    try {
      const idToken = await getFirebaseIdToken();
      if (!idToken) throw new Error("Authentication required.");
      const { apiBaseUrl } = getSquareConfig();

      const cancelResponse = await fetch(`${apiBaseUrl}/v1/billing/cancel-subscription`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
        body: JSON.stringify({
          account_id: account.id,
          // May be empty — the backend falls back to looking the real
          // subscription up via square_customer_id when this is missing or
          // stale (issue #60), so an account that never had this field
          // stored can still cancel.
          subscription_id: account.square_subscription_id ?? "",
        }),
      });

      if (!cancelResponse.ok) {
        const d = await cancelResponse.json().catch(() => ({})) as { error?: string };
        throw new Error(d.error ?? `Cancellation failed (${cancelResponse.status})`);
      }

      const { canceled_date } = await cancelResponse.json() as { canceled_date: string };

      if (state.appData) state.appData.account = await saveSubscriptionCanceled(account.id, canceled_date);

      state.billingModalOpen = null;
      setNotice("Your subscription is set to cancel — you'll keep access until your current billing period ends.");
      render();
    } catch (error) {
      if (btn) { btn.disabled = false; btn.textContent = "Cancel Subscription"; }
      if (errorEl) {
        errorEl.textContent = error instanceof Error ? error.message : "An unexpected error occurred.";
        errorEl.style.display = "";
      }
    }
  }

  async function initSquareBilling() {
    const container = mountNode.querySelector<HTMLElement>("#square-card-container");
    if (!container || squareCard) return;

    const config = getSquareConfig();
    if (!config.appId || config.appId.includes("REPLACE_ME")
      || !config.locationId || config.locationId.includes("REPLACE_ME")) {
      container.innerHTML = "<p class='muted'>Square credentials not configured — fill in VITE_SQUARE_APP_ID and VITE_SQUARE_LOCATION_ID in .env.dev1.</p>";
      return;
    }

    squareInitCancelled = false;

    if (!window.Square) {
      await new Promise<void>((resolve, reject) => {
        const script = document.createElement("script");
        script.src = config.sdkUrl;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Failed to load Square SDK"));
        document.head.appendChild(script);
      });
    }

    if (squareInitCancelled || !window.Square) return;

    try {
      const payments = await window.Square.payments(config.appId, config.locationId);
      if (squareInitCancelled) return;
      const card = await payments.card();
      if (squareInitCancelled) return;
      await card.attach("#square-card-container");
      squareCard = card;
    } catch (error) {
      if (!squareInitCancelled) {
        container.innerHTML = `<p class='muted'>Card form failed to load: ${error instanceof Error ? error.message : String(error)}</p>`;
      }
    }
  }

  async function handleDeleteAccount() {
    const accountId = state.appData?.account?.id;
    if (!accountId) return;

    const confirmed = window.confirm(
      "Delete all account data? This cannot be undone. Your login will also be removed so you can sign in fresh.",
    );
    if (!confirmed) return;

    setBusy(true);
    setError();
    setNotice();
    render();

    try {
      await deleteAccountData(accountId);
      await deleteCurrentUser();
      state.appData = null;
      state.csvResult = null;
      state.csvFileName = "";
      state.lastSavedUploadId = "";
      state.selectedUploadDetails = null;
      navigate("/");
    } catch (error) {
      setError(readError(error));
      setBusy(false);
      render();
    }
  }

  async function handleRefillTokens() {
    const accountId = state.appData?.account?.id;
    if (!accountId) return;

    setBusy(true);
    setError();
    setNotice();
    render();

    try {
      state.appData!.account = await refillTokens(accountId, 250);
      setNotice("Added 250 tokens.");
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleClearBatches() {
    const account = state.appData?.account;
    const profile = state.appData?.profile;
    if (!account || !profile) return;

    const confirmed = window.confirm(
      "Delete all upload batches and phone history for this location? Your profile and billing stay intact.",
    );
    if (!confirmed) return;

    setBusy(true);
    setError();
    setNotice();
    render();

    try {
      await clearBatchData(account.id, profile.id);
      state.csvResult = null;
      state.csvFileName = "";
      state.lastSavedUploadId = "";
      state.selectedUploadDetails = null;
      state.sendStatus = "idle";
      state.sendResult = null;
      await refreshAppData();
      setNotice("All batches and phone history cleared.");
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleClearPhoneBlockers(event: Event) {
    event.preventDefault();
    if (!state.isTesting) return;

    const account = state.appData?.account;
    const profile = state.appData?.profile;
    if (!account || !profile) {
      setError("Select a location before clearing a phone number.");
      render();
      return;
    }

    const form = (event.currentTarget as HTMLElement).closest("form") as HTMLFormElement | null;
    if (!form) return;

    const phonesRaw = String(new FormData(form).get("test_phone_number") ?? "").trim();
    if (!phonesRaw) {
      setError("Enter one or more phone numbers to clear.");
      render();
      return;
    }
    // Accept several numbers separated by commas, spaces, or new lines.
    const phoneInputs = phonesRaw.split(/[\s,]+/).filter(Boolean);

    setBusy(true);
    setError();
    setNotice();
    render();

    try {
      const { cleared, invalid } = await clearPhoneNumberBlockers(account.id, profile.id, phoneInputs);
      form.reset();
      await refreshAppData();
      const parts = [`Cleared blockers for ${cleared.length} number${cleared.length === 1 ? "" : "s"}: ${cleared.join(", ")}`];
      if (invalid.length > 0) parts.push(`skipped ${invalid.length} invalid (${invalid.join(", ")})`);
      setNotice(`${parts.join("; ")}. Phone log was kept.`);
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleLoadTestingAccounts() {
    if (!state.isTesting) return;
    state.testingAccountsLoading = true;
    render();
    try {
      state.testingAccounts = await loadTestingAccounts();
    } catch (error) {
      setError(readError(error));
    } finally {
      state.testingAccountsLoading = false;
      render();
    }
  }

  async function handleLoadAdminAccounts() {
    if (!isAdminEmail(state.auth.user?.email)) return;
    state.adminAccountsLoading = true;
    render();
    try {
      const idToken = await getFirebaseIdToken();
      if (!idToken) throw new Error("Authentication required.");
      const { apiBaseUrl } = getSquareConfig();
      const resp = await fetch(`${apiBaseUrl}/v1/admin/accounts`, {
        headers: { "Authorization": `Bearer ${idToken}` },
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({})) as { error?: string };
        throw new Error(data.error ?? `Failed to load accounts (${resp.status})`);
      }
      const data = await resp.json() as { accounts: AdminAccountOverview[] };
      state.adminAccounts = data.accounts;
    } catch (error) {
      setError(readError(error));
    } finally {
      state.adminAccountsLoading = false;
      render();
    }
  }

  async function handleLoadAdminActivityLog() {
    if (!isAdminEmail(state.auth.user?.email)) return;
    state.adminActivityLogLoading = true;
    render();
    try {
      const idToken = await getFirebaseIdToken();
      if (!idToken) throw new Error("Authentication required.");
      const { apiBaseUrl } = getSquareConfig();
      const resp = await fetch(`${apiBaseUrl}/v1/admin/activity-log`, {
        headers: { "Authorization": `Bearer ${idToken}` },
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({})) as { error?: string };
        throw new Error(data.error ?? `Failed to load activity log (${resp.status})`);
      }
      const data = await resp.json() as { entries: AdminActivityLogEntry[] };
      state.adminActivityLog = data.entries;
    } catch (error) {
      setError(readError(error));
    } finally {
      state.adminActivityLogLoading = false;
      render();
    }
  }

  async function handleLoadAdminPhoneNumbers() {
    if (!isAdminEmail(state.auth.user?.email)) return;
    state.adminPhoneNumbersLoading = true;
    render();
    try {
      const idToken = await getFirebaseIdToken();
      if (!idToken) throw new Error("Authentication required.");
      const { apiBaseUrl } = getSquareConfig();
      const resp = await fetch(`${apiBaseUrl}/v1/admin/phone-numbers`, {
        headers: { "Authorization": `Bearer ${idToken}` },
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({})) as { error?: string };
        throw new Error(data.error ?? `Failed to load phone numbers (${resp.status})`);
      }
      const data = await resp.json() as { numbers: AdminPhoneNumberEntry[] };
      state.adminPhoneNumbers = data.numbers;
    } catch (error) {
      setError(readError(error));
    } finally {
      state.adminPhoneNumbersLoading = false;
      render();
    }
  }

  async function handleLoadAdminOptOuts() {
    if (!isAdminEmail(state.auth.user?.email)) return;
    state.adminOptOutsLoading = true;
    render();
    try {
      const idToken = await getFirebaseIdToken();
      if (!idToken) throw new Error("Authentication required.");
      const { apiBaseUrl } = getSquareConfig();
      const resp = await fetch(`${apiBaseUrl}/v1/admin/opt-outs`, {
        headers: { "Authorization": `Bearer ${idToken}` },
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({})) as { error?: string };
        throw new Error(data.error ?? `Failed to load opt-outs (${resp.status})`);
      }
      const data = await resp.json() as { opt_outs: AdminOptOutEntry[] };
      state.adminOptOuts = data.opt_outs;
    } catch (error) {
      setError(readError(error));
    } finally {
      state.adminOptOutsLoading = false;
      render();
    }
  }

  async function handleLoadAdminTransactions() {
    if (!isAdminEmail(state.auth.user?.email)) return;
    state.adminTransactionsLoading = true;
    render();
    try {
      const idToken = await getFirebaseIdToken();
      if (!idToken) throw new Error("Authentication required.");
      const { apiBaseUrl } = getSquareConfig();
      const resp = await fetch(`${apiBaseUrl}/v1/admin/transactions`, {
        headers: { "Authorization": `Bearer ${idToken}` },
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({})) as { error?: string };
        throw new Error(data.error ?? `Failed to load transactions (${resp.status})`);
      }
      const data = await resp.json() as AdminTransactionsData;
      state.adminTransactions = data;
    } catch (error) {
      setError(readError(error));
    } finally {
      state.adminTransactionsLoading = false;
      render();
    }
  }

  async function handleLoadAdminCustomerStats(accountId: string, range: AdminCustomerStatsRange) {
    if (!isAdminEmail(state.auth.user?.email)) return;
    state.adminCustomerStatsLoading = true;
    render();
    try {
      const idToken = await getFirebaseIdToken();
      if (!idToken) throw new Error("Authentication required.");
      const { apiBaseUrl } = getSquareConfig();
      const params = new URLSearchParams({ account_id: accountId, range });
      const resp = await fetch(`${apiBaseUrl}/v1/admin/customer-stats?${params.toString()}`, {
        headers: { "Authorization": `Bearer ${idToken}` },
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({})) as { error?: string };
        throw new Error(data.error ?? `Failed to load customer stats (${resp.status})`);
      }
      state.adminCustomerStats = await resp.json() as AdminCustomerStatsData;
    } catch (error) {
      setError(readError(error));
    } finally {
      state.adminCustomerStatsLoading = false;
      render();
    }
  }

  // handleAdminTabClick switches the active admin tab and lazily fetches
  // that tab's data on first visit (no URL change — tabs are client-side
  // state within the single /admin route, not separate pages).
  function handleAdminTabClick(tab: AdminTab) {
    state.adminTab = tab;
    render();
    if (tab === "accounts" && state.adminAccounts === null) handleLoadAdminAccounts();
    if (tab === "activity-log" && state.adminActivityLog === null) handleLoadAdminActivityLog();
    if (tab === "phone-numbers" && state.adminPhoneNumbers === null) handleLoadAdminPhoneNumbers();
    if (tab === "opt-outs" && state.adminOptOuts === null) handleLoadAdminOptOuts();
    if (tab === "transactions" && state.adminTransactions === null) handleLoadAdminTransactions();
    if (tab === "customer-stats") {
      // Reuses the accounts tab's data to populate the customer picker,
      // rather than fetching the full account list a second time.
      if (state.adminAccounts === null) handleLoadAdminAccounts();
      if (state.adminCustomerStatsAccountId && state.adminCustomerStats === null) {
        handleLoadAdminCustomerStats(state.adminCustomerStatsAccountId, state.adminCustomerStatsRange);
      }
    }
  }

  function handleAdminCustomerStatsAccountChange(accountId: string) {
    state.adminCustomerStatsAccountId = accountId || null;
    state.adminCustomerStats = null;
    render();
    if (accountId) handleLoadAdminCustomerStats(accountId, state.adminCustomerStatsRange);
  }

  function handleAdminCustomerStatsRangeChange(range: AdminCustomerStatsRange) {
    if (state.adminCustomerStatsRange === range) return;
    state.adminCustomerStatsRange = range;
    state.adminCustomerStats = null;
    render();
    if (state.adminCustomerStatsAccountId) handleLoadAdminCustomerStats(state.adminCustomerStatsAccountId, range);
  }

  async function handleRevertTester() {
    const email = state.auth.user?.email;
    if (!email) return;
    if (!confirm(`Remove ${email} from testing accounts?`)) return;

    setBusy(true);
    setError();
    render();
    try {
      await removeTestingAccount(email);
      // refreshAppData recomputes isTesting/activeAccountIsTesting itself
      // (checkIsTestingAccount) — no manual page reload needed (issue #65).
      await refreshAppData();
      setNotice("Removed from testing accounts.");
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleRemoveTestingAccount(email: string) {
    if (!confirm(`Remove ${email} from testing accounts?`)) return;
    setBusy(true);
    setError();
    render();
    try {
      await removeTestingAccount(email);
      state.testingAccounts = await loadTestingAccounts();
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleAddTestingAccount(event: Event) {
    event.preventDefault();
    if (!state.isTesting || !state.auth.user?.email) return;
    const form = (event.currentTarget as HTMLElement).closest("form") as HTMLFormElement | null;
    if (!form) return;
    const email = String(new FormData(form).get("new_testing_email") ?? "").trim().toLowerCase();
    if (!email) return;

    setBusy(true);
    setError();
    render();
    try {
      await addTestingAccount(email, state.auth.user.email);
      form.reset();
      state.testingAccounts = await loadTestingAccounts();
      setNotice(`${email} added as a testing account.`);
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleLoadAuthorizedUsers() {
    const account = state.appData?.account;
    if (!account || state.guestAccountId) return;
    state.authorizedUsersLoading = true;
    render();
    try {
      state.authorizedUsers = await loadAuthorizedUsers(account.id);
    } catch (error) {
      setError(`Failed to load team members: ${readError(error)}`);
    } finally {
      state.authorizedUsersLoading = false;
      render();
    }
  }

  async function handleAddAuthorizedUser(event: Event) {
    event.preventDefault();
    const account = state.appData?.account;
    const ownerEmail = state.auth.user?.email;
    if (!account || !ownerEmail || state.guestAccountId) return;
    const form = (event.currentTarget as HTMLElement).closest("form") as HTMLFormElement | null;
    if (!form) return;
    const email = String(new FormData(form).get("new_authorized_email") ?? "").trim().toLowerCase();
    if (!email) {
      setError("Enter an email address before clicking Add to team.");
      render();
      return;
    }

    setBusy(true);
    setError();
    render();
    try {
      await addAuthorizedUser(account.id, ownerEmail, email, ownerEmail);
      form.reset();
      state.authorizedUsers = await loadAuthorizedUsers(account.id);
      setNotice(`${email} added to your team.`);
    } catch (error) {
      setError(`Failed to add team member: ${readError(error)}`);
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleRemoveAuthorizedUser(email: string) {
    const account = state.appData?.account;
    if (!account || state.guestAccountId) return;
    setBusy(true);
    setError();
    render();
    try {
      await removeAuthorizedUser(account.id, email);
      state.authorizedUsers = await loadAuthorizedUsers(account.id);
      setNotice(`${email} removed from your team.`);
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleSwitchToGuestAccount(access: AccountAccess) {
    state.guestAccountId = access.owner_account_id;
    state.guestOwnerEmail = access.owner_email;
    localStorage.setItem(guestAccountStorageKey, access.owner_account_id);
    localStorage.setItem(guestOwnerEmailStorageKey, access.owner_email);
    state.selectedProfileId = null;
    sessionStorage.removeItem("selectedProfileId");
    setNotice(`Switched to ${access.owner_email}'s account.`);
    await refreshAppData();
  }

  function handleSwitchBackToOwnAccount() {
    state.guestAccountId = null;
    state.guestOwnerEmail = null;
    localStorage.removeItem(guestAccountStorageKey);
    localStorage.removeItem(guestOwnerEmailStorageKey);
    state.selectedProfileId = null;
    sessionStorage.removeItem("selectedProfileId");
    setNotice("Switched back to your account.");
    refreshAppData();
  }

  async function handleSendAll() {
    if (!state.appData?.account || !state.appData.profile) {
      setError("No profile selected. Try refreshing the page.");
      render();
      return;
    }

    const account = state.appData.account;
    const profile = state.appData.profile;

    // "validated" uploads need markUploadReady first; "ready" uploads already have queued rows
    const validatedUploads = state.appData.uploads.filter(
      (u) => u.status === "validated" && u.valid_rows > 0,
    );
    const readyUploads = state.appData.uploads.filter(
      (u) => u.status === "ready" && u.queued_rows > 0,
    );
    const allSendable = [...validatedUploads, ...readyUploads];

    if (allSendable.length === 0) {
      setError(`No batches ready to send. Batch statuses: ${state.appData.uploads.map((u) => `${u.status}(queued=${u.queued_rows})`).join(", ") || "none"}`);
      render();
      return;
    }

    if (!state.activeAccountIsTesting && (!account.square_card_id || account.billing_status !== "active")) {
      setError(`Billing not active (status: ${account.billing_status}). Set up billing before sending.`);
      render();
      return;
    }

    console.log(`[send] starting — ${allSendable.length} batch(es), account=${account.id}, profile=${profile.id}`);
    setBusy(true);
    setError();
    setNotice();
    state.sendStatus = "sending";
    state.sendResult = null;
    state.sendAbortController = new AbortController();
    render();

    try {
      const idToken = await getFirebaseIdToken();
      if (!idToken) throw new Error("Authentication required.");
      const { apiBaseUrl } = getSquareConfig();

      // Cross-customer opt-out check (issue #38). Fails open (treats the
      // batch as if nothing were opted out) if the check itself errors —
      // sendBatch still enforces this server-side, so a transient failure
      // here only means the user misses the earlier upload-time warning.
      const checkGlobalOptOut = async (phones: string[]): Promise<Set<string>> => {
        try {
          const resp = await fetch(`${apiBaseUrl}/v1/optout/check`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${idToken}`,
            },
            body: JSON.stringify({ phones }),
          });
          if (!resp.ok) {
            console.warn(`[send] global opt-out check failed (${resp.status}), proceeding without it`);
            return new Set<string>();
          }
          const data = await resp.json() as { opted_out?: Record<string, boolean> };
          return new Set(Object.entries(data.opted_out ?? {}).filter(([, optedOut]) => optedOut).map(([phone]) => phone));
        } catch (err) {
          console.warn("[send] global opt-out check failed, proceeding without it", err);
          return new Set<string>();
        }
      };

      // Stage any validated uploads (applies global opt-out + suppression window checks, sets rows to "queued")
      for (const upload of validatedUploads) {
        console.log(`[send] staging upload ${upload.id}`);
        await markUploadReady(account.id, profile.id, upload.id, checkGlobalOptOut);
      }

      // Tell Go to send — it reads all rows with status "queued" for each upload
      console.log(`[send] calling /v1/sms/send-batch with ${allSendable.length} upload(s)`);

      const resp = await fetch(`${apiBaseUrl}/v1/sms/send-batch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          account_id: account.id,
          profile_id: profile.id,
          upload_ids: allSendable.map((u) => u.id),
        }),
        signal: state.sendAbortController.signal,
      });

      if (!resp.ok) {
        const data = await resp.json().catch(() => ({})) as { error?: string };
        throw new Error(data.error ?? `Send failed (${resp.status})`);
      }

      const result = await resp.json() as SendResult;
      console.log(`[send] enqueued=${result.enqueued}, skipped=${result.skipped}, failed=${result.failed}, total=${result.total}`);
      result.results?.forEach((r) => {
        if (r.status !== "enqueued") console.warn(`[send] ${r.phone}: ${r.status}${r.reason ? " — " + r.reason : ""}`);
      });
      // The worker sends the messages and bills tokens as it goes; the client no
      // longer deducts tokens. The balance updates as the poll refreshes data.
      state.sendResult = result;
      state.sendStatus = result.failed > 0 && result.enqueued === 0 ? "failed" : "complete";
      await refreshAppData();
      // Rows now advance in the background (opt-in → response → link). Poll so the
      // batch table and token balance reflect the worker's progress.
      if (result.enqueued > 0) startSendPoll();
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        console.log("[send] cancelled by user");
        state.sendStatus = "stopped";
      } else {
        console.error("[send] batch failed:", error);
        setError(readError(error));
        state.sendStatus = "failed";
      }
    } finally {
      state.sendAbortController = null;
      setBusy(false);
      render();
    }
  }

  function handleStopSend() {
    state.sendAbortController?.abort();
  }

  const billingPollIntervalMs = 10_000;
  const billingPollMaxTicks = 30; // ~5 minutes

  function startBillingPoll() {
    stopBillingPoll();
    state.billingPollTicks = 0;
    state.billingPollTimer = setInterval(pollBillingStatus, billingPollIntervalMs);
  }

  function stopBillingPoll() {
    if (state.billingPollTimer !== null) {
      clearInterval(state.billingPollTimer);
      state.billingPollTimer = null;
    }
  }

  // After a subscription purchase, Square's invoice.payment_made webhook —
  // not the /v1/billing/subscribe call itself — is what flips billing_status
  // to "active" and credits tokens (see saveSubscriptionCreated in
  // src/lib/data.ts). That webhook is async and outside the frontend's
  // control, so this polls refreshAppData until it lands (or times out),
  // rather than leaving the dashboard/billing page showing pre-purchase
  // numbers until a manual reload (issue #65).
  async function pollBillingStatus() {
    state.billingPollTicks++;
    if (state.billingPollTicks > billingPollMaxTicks || !state.auth.user) {
      stopBillingPoll();
      return;
    }
    try {
      await refreshAppData();
      if (state.appData?.account?.billing_status === "active") {
        stopBillingPoll();
      }
    } catch (error) {
      console.warn("[billing] poll failed:", error);
    }
  }

  // Background progress poll. After enqueue the worker drives each row forward
  // (opt-in → response → link) and bills tokens; polling reloads app data so the
  // batch table and balance track that without a manual refresh. Bounded so it
  // never runs forever; the user can leave the page at any time (stopSendPoll).
  const sendPollIntervalMs = 10_000;
  const sendPollMaxTicks = 30; // ~5 minutes

  function startSendPoll() {
    stopSendPoll();
    state.sendPollTicks = 0;
    state.sendPollTimer = setInterval(pollSendProgress, sendPollIntervalMs);
  }

  function stopSendPoll() {
    if (state.sendPollTimer !== null) {
      clearInterval(state.sendPollTimer);
      state.sendPollTimer = null;
    }
  }

  async function pollSendProgress() {
    state.sendPollTicks++;
    if (state.sendPollTicks > sendPollMaxTicks || !state.auth.user || !state.appData?.profile) {
      stopSendPoll();
      return;
    }
    try {
      const data = await loadAppData(
        state.guestAccountId ?? state.auth.user.id,
        state.auth.user.email ?? null,
        state.selectedProfileId ?? undefined,
      );
      state.appData = data;
      render();
    } catch (error) {
      console.warn("[send] poll failed:", error);
    }
  }

  async function handleManualAdd() {
    const account = state.appData?.account;
    const profile = state.appData?.profile;
    if (!account || !profile) return;

    const textarea = mountNode.querySelector<HTMLTextAreaElement>("[data-manual-text]");
    const text = textarea?.value.trim() ?? "";
    if (!text) {
      setError("Enter at least one phone number.");
      render();
      return;
    }

    setBusy(true);
    setError();
    setNotice();
    render();

    try {
      const batchNameInput = mountNode.querySelector<HTMLInputElement>("[data-batch-name]");
      const batchName = batchNameInput?.value.trim() || undefined;
      const attestedBy = state.auth.user?.email ?? "";
      const validation = parsePhoneListText(text, attestedBy, state.activeAccountIsTesting);
      const saved = await saveManualBatch(account.id, profile.id, validation, batchName);
      state.selectedUploadDetails = saved;
      state.lastSavedUploadId = saved.upload.id;
      state.uploadMode = "file";
      await refreshAppData();
      setNotice(`${validation.summary.validRows} number${validation.summary.validRows === 1 ? "" : "s"} ready, ${validation.summary.rejectedRows} rejected.`);
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleAddMoreRows(uploadId: string) {
    const account = state.appData?.account;
    const profile = state.appData?.profile;
    if (!account || !profile) return;

    const textarea = mountNode.querySelector<HTMLTextAreaElement>("[data-add-more-text]");
    const text = textarea?.value.trim() ?? "";
    if (!text) {
      setError("Enter at least one phone number.");
      render();
      return;
    }

    setBusy(true);
    setError();
    setNotice();
    render();

    try {
      const attestedBy = state.auth.user?.email ?? "";
      const validation = parsePhoneListText(text, attestedBy, state.activeAccountIsTesting);
      state.selectedUploadDetails = await addRowsToBatch(account.id, profile.id, uploadId, validation);
      await refreshAppData();
      setNotice(`Added ${validation.summary.validRows} number${validation.summary.validRows === 1 ? "" : "s"}.`);
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleRemoveRow(uploadId: string, rowId: string) {
    const account = state.appData?.account;
    const profile = state.appData?.profile;
    if (!account || !profile) return;

    setBusy(true);
    setError();
    setNotice();
    render();

    try {
      state.selectedUploadDetails = await removeRowFromBatch(account.id, profile.id, uploadId, rowId);
      await refreshAppData();
    } catch (error) {
      setError(readError(error));
    } finally {
      setBusy(false);
      render();
    }
  }

  async function handleSelectProfile(profileId: string) {
    console.log(`[profile] selecting ${profileId}`);
    state.selectedProfileId = profileId;
    sessionStorage.setItem("selectedProfileId", profileId);
    await refreshAppData();
    navigate("/");
  }

  async function handleCreateProfile(event: SubmitEvent) {
    event.preventDefault();
    const account = state.appData?.account;
    if (!account) return;

    const form = event.currentTarget as HTMLFormElement;
    const locationName = String(new FormData(form).get("location_name") ?? "").trim();
    if (!locationName) {
      setError("Enter a location name.");
      render();
      return;
    }

    console.log(`[profile] creating location "${locationName}"`);
    setBusy(true);
    setError();
    setNotice();
    render();

    try {
      const profile = await createProfile(account.id, locationName);
      console.log(`[profile] created ${profile.id} ("${profile.location_name}")`);
      state.selectedProfileId = profile.id;
      sessionStorage.setItem("selectedProfileId", profile.id);
      await refreshAppData();
      navigate("/profile");
    } catch (error) {
      console.error("[profile] create failed:", error);
      setError(readError(error));
      setBusy(false);
      render();
    }
  }

  function handleSwitchProfile() {
    console.log("[profile] switching profile — clearing selection");
    state.selectedProfileId = null;
    sessionStorage.removeItem("selectedProfileId");
    navigate("/profile-select");
  }

  function bindEvents() {
    mountNode.querySelectorAll<HTMLAnchorElement>("[data-nav]").forEach((node) => {
      node.addEventListener("click", (event) => {
        event.preventDefault();
        navigate(node.getAttribute("href") || "/");
      });
    });

    mountNode.querySelectorAll<HTMLButtonElement>("[data-admin-tab]").forEach((node) => {
      node.addEventListener("click", () => {
        handleAdminTabClick(node.getAttribute("data-admin-tab") as AdminTab);
      });
    });

    mountNode
      .querySelector<HTMLSelectElement>("[data-admin-customer-select]")
      ?.addEventListener("change", (event) => {
        handleAdminCustomerStatsAccountChange((event.currentTarget as HTMLSelectElement).value);
      });

    mountNode.querySelectorAll<HTMLButtonElement>("[data-admin-range]").forEach((node) => {
      node.addEventListener("click", () => {
        handleAdminCustomerStatsRangeChange(node.getAttribute("data-admin-range") as AdminCustomerStatsRange);
      });
    });

    mountNode.querySelectorAll<HTMLInputElement>("[data-admin-filter]").forEach((node) => {
      node.addEventListener("input", () => {
        const which = node.getAttribute("data-admin-filter");
        const pos = node.selectionStart;
        if (which === "activity-log") state.adminActivityLogFilter = node.value;
        if (which === "phone-numbers") state.adminPhoneNumbersFilter = node.value;
        render();
        // Re-render replaces the DOM, so restore focus + caret to the filter box.
        const again = mountNode.querySelector<HTMLInputElement>(`[data-admin-filter="${which}"]`);
        if (again) {
          again.focus();
          if (pos != null) again.setSelectionRange(pos, pos);
        }
      });
    });

    mountNode
      .querySelector<HTMLButtonElement>("[data-google-login]")
      ?.addEventListener("click", handleGoogleSignIn);

    mountNode
      .querySelector<HTMLFormElement>("[data-magic-link-form]")
      ?.addEventListener("submit", handleMagicLink);

    mountNode
      .querySelector<HTMLButtonElement>("[data-sign-out]")
      ?.addEventListener("click", handleSignOut);

    mountNode
      .querySelector<HTMLButtonElement>("[data-save-profile]")
      ?.addEventListener("click", handleProfileSave);

    mountNode
      .querySelector<HTMLInputElement>("[data-attest-checkbox]")
      ?.addEventListener("change", (event) => {
        state.uploadAttested = (event.currentTarget as HTMLInputElement).checked;
        render();
      });

    mountNode
      .querySelector<HTMLInputElement>("[data-file-input]")
      ?.addEventListener("change", handleFileSelected);

    mountNode.querySelectorAll<HTMLButtonElement>("[data-upload-tab]").forEach((btn) => {
      btn.addEventListener("click", () => {
        state.uploadMode = btn.dataset.uploadTab as "file" | "manual";
        render();
      });
    });

    mountNode
      .querySelector<HTMLButtonElement>("[data-manual-add]")
      ?.addEventListener("click", handleManualAdd);

    mountNode
      .querySelector<HTMLButtonElement>("[data-add-more-rows]")
      ?.addEventListener("click", () => {
        const btn = mountNode.querySelector<HTMLButtonElement>("[data-add-more-rows]");
        const uploadId = btn?.dataset.addMoreRows ?? "";
        if (uploadId) handleAddMoreRows(uploadId);
      });

    mountNode.querySelectorAll<HTMLButtonElement>("[data-remove-row]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const uploadId = btn.dataset.removeRow ?? "";
        const rowId = btn.dataset.rowId ?? "";
        if (uploadId && rowId) handleRemoveRow(uploadId, rowId);
      });
    });

    mountNode.querySelectorAll<HTMLButtonElement>("[data-select-upload]").forEach((node) => {
      node.addEventListener("click", handleSelectUpload);
    });

    mountNode.querySelectorAll<HTMLButtonElement>("[data-close-upload]").forEach((btn) => {
      btn.addEventListener("click", async () => {
        const uploadId = btn.dataset.closeUpload ?? "";
        const account = state.appData?.account;
        const profile = state.appData?.profile;
        if (!uploadId || !account || !profile) return;
        if (!confirm("Close this batch? It will be hidden from active batches and excluded from counts.")) return;
        setBusy(true);
        try {
          await closeUpload(account.id, profile.id, uploadId);
          await refreshAppData();
        } catch (error) {
          setError(readError(error));
        } finally {
          setBusy(false);
          render();
        }
      });
    });

    mountNode
      .querySelector<HTMLButtonElement>("[data-toggle-closed-batches]")
      ?.addEventListener("click", () => {
        state.showClosedBatches = !state.showClosedBatches;
        render();
      });

    mountNode
      .querySelector<HTMLSelectElement>("[data-dashboard-batch-select]")
      ?.addEventListener("change", (event) => {
        state.dashboardBatchId = (event.currentTarget as HTMLSelectElement).value;
        render();
      });

    mountNode
      .querySelector<HTMLButtonElement>("[data-send-all]")
      ?.addEventListener("click", handleSendAll);

    mountNode
      .querySelector<HTMLButtonElement>("[data-stop-send]")
      ?.addEventListener("click", handleStopSend);

    mountNode.querySelectorAll<HTMLButtonElement>("[data-toggle-raw]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const wrapper = btn.closest(".table-wrapper");
        if (!wrapper) return;
        const showing = wrapper.classList.toggle("show-raw");
        btn.textContent = showing ? "Hide original numbers" : "Show original numbers";
      });
    });

    mountNode.querySelectorAll<HTMLButtonElement>("[data-select-profile]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const profileId = btn.dataset.selectProfile ?? "";
        if (profileId) handleSelectProfile(profileId);
      });
    });

    mountNode
      .querySelector<HTMLFormElement>("[data-create-profile-form]")
      ?.addEventListener("submit", handleCreateProfile);

    mountNode
      .querySelector<HTMLButtonElement>("[data-switch-profile]")
      ?.addEventListener("click", handleSwitchProfile);

    mountNode
      .querySelector<HTMLButtonElement>("[data-delete-account]")
      ?.addEventListener("click", handleDeleteAccount);

    mountNode
      .querySelector<HTMLButtonElement>("[data-clear-batches]")
      ?.addEventListener("click", handleClearBatches);

    mountNode
      .querySelector<HTMLButtonElement>("[data-refill-tokens]")
      ?.addEventListener("click", handleRefillTokens);

    mountNode
      .querySelector<HTMLSelectElement>("[data-log-phone-filter]")
      ?.addEventListener("change", (event) => {
        state.logPhoneFilter = (event.currentTarget as HTMLSelectElement).value;
        render();
      });

    mountNode
      .querySelector<HTMLInputElement>("[data-number-search]")
      ?.addEventListener("input", (event) => {
        const el = event.currentTarget as HTMLInputElement;
        const pos = el.selectionStart;
        state.numberHistoryFilter = el.value;
        render();
        // Re-render replaces the DOM, so restore focus + caret to the search box.
        const again = mountNode.querySelector<HTMLInputElement>("[data-number-search]");
        if (again) {
          again.focus();
          if (pos != null) again.setSelectionRange(pos, pos);
        }
      });

    mountNode
      .querySelectorAll<HTMLButtonElement>("[data-view-number]")
      .forEach((node) => {
        node.addEventListener("click", () => {
          handleViewNumberInLog(node.dataset.viewNumber ?? "");
        });
      });

    mountNode
      .querySelector<HTMLButtonElement>("[data-clear-phone-blockers]")
      ?.addEventListener("click", handleClearPhoneBlockers);

    mountNode
      .querySelector<HTMLButtonElement>("[data-billing-submit]")
      ?.addEventListener("click", handleBillingSubmit);

    mountNode
      .querySelector<HTMLButtonElement>("[data-add-testing-account]")
      ?.addEventListener("click", handleAddTestingAccount);

    mountNode
      .querySelector<HTMLButtonElement>("[data-revert-tester]")
      ?.addEventListener("click", handleRevertTester);

    mountNode.querySelectorAll<HTMLButtonElement>("[data-remove-testing-account]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const email = btn.dataset.removeTestingAccount ?? "";
        if (email) handleRemoveTestingAccount(email);
      });
    });

    mountNode
      .querySelector<HTMLButtonElement>("[data-add-authorized-user]")
      ?.addEventListener("click", handleAddAuthorizedUser);

    mountNode.querySelectorAll<HTMLButtonElement>("[data-remove-authorized-user]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const email = btn.dataset.removeAuthorizedUser ?? "";
        if (email) handleRemoveAuthorizedUser(email);
      });
    });

    mountNode.querySelectorAll<HTMLButtonElement>("[data-switch-guest-account]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const idx = Number(btn.dataset.switchGuestAccount ?? "0");
        const access = state.linkedAccounts?.[idx];
        if (access) handleSwitchToGuestAccount(access);
      });
    });

    mountNode
      .querySelector<HTMLButtonElement>("[data-switch-own-account]")
      ?.addEventListener("click", handleSwitchBackToOwnAccount);

    mountNode
      .querySelector<HTMLButtonElement>("[data-update-card]")
      ?.addEventListener("click", () => {
        state.billingShowUpdateForm = true;
        render();
      });

    mountNode.querySelectorAll<HTMLButtonElement>("[data-plan-id]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const planId = btn.dataset.planId ?? "";
        if (planId) {
          state.billingSelectedPlanId = planId;
          state.billingConfirmChecked = false;
          render();
        }
      });
    });

    mountNode
      .querySelector<HTMLButtonElement>("[data-plan-open]")
      ?.addEventListener("click", () => {
        state.billingConfirmChecked = false;
        state.billingModalOpen = "plan";
        render();
      });

    mountNode
      .querySelector<HTMLButtonElement>("[data-demo-open]")
      ?.addEventListener("click", () => {
        state.billingDemoConfirmChecked = false;
        state.billingModalOpen = "demo";
        render();
      });

    mountNode
      .querySelector<HTMLButtonElement>("[data-cancel-open]")
      ?.addEventListener("click", () => {
        state.billingModalOpen = "cancel";
        render();
      });

    mountNode.querySelectorAll<HTMLElement>("[data-close-billing-modal-btn]").forEach((el) => {
      el.addEventListener("click", () => {
        state.billingModalOpen = null;
        render();
      });
    });

    mountNode.querySelectorAll<HTMLElement>("[data-close-billing-modal]").forEach((el) => {
      el.addEventListener("click", (e) => {
        if (e.target !== e.currentTarget) return;
        state.billingModalOpen = null;
        render();
      });
    });

    mountNode
      .querySelector<HTMLInputElement>("#billing-confirm-checkbox")
      ?.addEventListener("change", (e) => {
        state.billingConfirmChecked = (e.target as HTMLInputElement).checked;
        const confirmBtn = mountNode.querySelector<HTMLButtonElement>("[data-confirm-purchase]");
        if (confirmBtn) confirmBtn.disabled = !state.billingConfirmChecked;
      });

    mountNode
      .querySelector<HTMLButtonElement>("[data-confirm-purchase]")
      ?.addEventListener("click", handleConfirmPurchase);

    mountNode
      .querySelector<HTMLInputElement>("#demo-confirm-checkbox")
      ?.addEventListener("change", (e) => {
        state.billingDemoConfirmChecked = (e.target as HTMLInputElement).checked;
        const demoBtn = mountNode.querySelector<HTMLButtonElement>("[data-demo-confirm]");
        if (demoBtn) demoBtn.disabled = !state.billingDemoConfirmChecked;
      });

    mountNode
      .querySelector<HTMLButtonElement>("[data-demo-confirm]")
      ?.addEventListener("click", handleDemoCharge);

    mountNode
      .querySelector<HTMLButtonElement>("[data-cancel-confirm]")
      ?.addEventListener("click", handleCancelSubscription);

    if (state.route === "billing" && mountNode.querySelector("#square-card-container")) {
      initSquareBilling().catch(() => {});
    }

    // Live profile field badge updates
    const profileForm = mountNode.querySelector<HTMLFormElement>("[data-profile-form]");
    if (profileForm) {
      function setFieldBadge(key: string, filled: boolean) {
        const badge = profileForm!.querySelector<HTMLElement>(`[data-field-badge="${key}"]`);
        if (!badge) return;
        badge.className = `field-status-badge ${filled ? "badge-done" : "badge-needed"}`;
        badge.textContent = filled ? "✓" : "✗";
      }

      function syncPlatformBadge() {
        const any = pendingPlatforms.some((p) => p.enabled && p.url.trim());
        setFieldBadge("review_platforms", any);
      }

      for (const name of ["business_name", "business_address", "business_phone", "contact_name", "primary_email"]) {
        profileForm.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[name="${name}"]`)
          ?.addEventListener("input", (e) =>
            setFieldBadge(name, Boolean((e.currentTarget as HTMLInputElement).value.trim())),
          );
      }

      bindPlatformEditorEvents(syncPlatformBadge);
    }
  }

  function bindPlatformEditorEvents(syncBadge: () => void) {
    const editor = mountNode.querySelector<HTMLElement>("[data-platform-editor]");
    if (!editor) return;

    editor.querySelectorAll<HTMLInputElement>("[data-toggle-platform]").forEach((cb) => {
      cb.addEventListener("change", () => {
        const idx = Number(cb.dataset.togglePlatform);
        if (pendingPlatforms[idx]) pendingPlatforms[idx].enabled = cb.checked;
        syncBadge();
      });
    });

    editor.querySelectorAll<HTMLInputElement>("[data-platform-url]").forEach((input) => {
      input.addEventListener("input", () => {
        const idx = Number(input.dataset.platformUrl);
        if (pendingPlatforms[idx]) pendingPlatforms[idx].url = input.value;
        syncBadge();
      });
    });

    editor.querySelectorAll<HTMLInputElement>("[data-platform-name]").forEach((input) => {
      input.addEventListener("input", () => {
        const idx = Number(input.dataset.platformName);
        if (pendingPlatforms[idx]) pendingPlatforms[idx].name = input.value;
      });
    });

    editor.querySelectorAll<HTMLButtonElement>("[data-remove-platform]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const idx = Number(btn.dataset.removePlatform);
        pendingPlatforms.splice(idx, 1);
        refreshPlatformEditor(syncBadge);
      });
    });

    mountNode.querySelector<HTMLButtonElement>("[data-add-platform]")
      ?.addEventListener("click", () => {
        pendingPlatforms.push({ id: `custom_${Date.now()}`, name: "Custom", url: "", enabled: true });
        refreshPlatformEditor(syncBadge);
      });
  }

  function refreshPlatformEditor(syncBadge: () => void) {
    const editor = mountNode.querySelector<HTMLElement>("[data-platform-editor]");
    if (!editor) return;
    editor.innerHTML = renderPlatformEditorRows(pendingPlatforms);
    bindPlatformEditorEvents(syncBadge);
    syncBadge();
  }

  function render() {
    squareInitCancelled = true;
    if (squareCard) {
      squareCard.destroy().catch(() => {});
      squareCard = null;
    }

    const pathname = window.location.pathname;

    // Public routes — bypass auth entirely. render() runs multiple times on
    // initial load (sync call, getAuthSnapshot().then, onAuthStateChange), and
    // each one used to re-fetch/re-log the click unconditionally — 3 requests
    // from one page load, logged as 3 separate activity-log entries (issue #67).
    // Only (re-)initialize the public page the first time we see this pathname.
    if (pathname.startsWith("/r/") || pathname.startsWith("/f/")) {
      if (lastPublicPagePath !== pathname) {
        lastPublicPagePath = pathname;
        mountNode.innerHTML = renderPublicShell(pathname);
        initPublicPage(pathname);
      }
      return;
    }

    // Reset pending platforms each time the profile page renders from scratch
    if (state.route === "profile") {
      const saved = state.appData?.profile?.review_platforms;
      pendingPlatforms = saved && saved.length > 0
        ? saved.map((p) => ({ ...p }))
        : [...defaultReviewPlatforms];
    }

    mountNode.innerHTML = state.auth.user
      ? (state.route === "admin" && isAdminEmail(state.auth.user.email)
          ? renderAdminShell({
              auth: state.auth,
              adminTab: state.adminTab,
              adminAccounts: state.adminAccounts,
              adminAccountsLoading: state.adminAccountsLoading,
              adminActivityLog: state.adminActivityLog,
              adminActivityLogLoading: state.adminActivityLogLoading,
              adminActivityLogFilter: state.adminActivityLogFilter,
              adminPhoneNumbers: state.adminPhoneNumbers,
              adminPhoneNumbersLoading: state.adminPhoneNumbersLoading,
              adminPhoneNumbersFilter: state.adminPhoneNumbersFilter,
              adminOptOuts: state.adminOptOuts,
              adminOptOutsLoading: state.adminOptOutsLoading,
              adminTransactions: state.adminTransactions,
              adminTransactionsLoading: state.adminTransactionsLoading,
              adminCustomerStatsAccountId: state.adminCustomerStatsAccountId,
              adminCustomerStatsRange: state.adminCustomerStatsRange,
              adminCustomerStats: state.adminCustomerStats,
              adminCustomerStatsLoading: state.adminCustomerStatsLoading,
              notice: state.notice,
              error: state.error,
            })
          : renderAuthedApp({ ...state, pendingPlatforms, selectedProfileId: state.selectedProfileId, phoneLog: state.phoneLog, phoneLogLoading: state.phoneLogLoading, isTesting: state.isTesting, activeAccountIsTesting: state.activeAccountIsTesting, testingAccounts: state.testingAccounts, testingAccountsLoading: state.testingAccountsLoading, authorizedUsers: state.authorizedUsers, authorizedUsersLoading: state.authorizedUsersLoading, linkedAccounts: state.linkedAccounts, guestAccountId: state.guestAccountId, guestOwnerEmail: state.guestOwnerEmail }))
      : renderAuthLanding(state);
    bindEvents();
  }

  function initPublicPage(pathname: string) {
    const { apiBaseUrl } = getSquareConfig();
    if (pathname.startsWith("/r/")) {
      const token = pathname.slice("/r/".length);
      fetchReviewToken(apiBaseUrl, token);
    } else if (pathname.startsWith("/f/")) {
      const token = pathname.slice("/f/".length);
      fetchFeedbackToken(apiBaseUrl, token);
      bindPublicFeedbackForm(apiBaseUrl, token);
    }
  }

  async function fetchReviewToken(apiBaseUrl: string, token: string) {
    const container = mountNode.querySelector<HTMLElement>("#public-content");
    if (!container) return;
    try {
      const resp = await fetch(`${apiBaseUrl}/v1/r/${encodeURIComponent(token)}`);
      if (!resp.ok) throw new Error("Review link not found or expired.");
      const data = await resp.json() as { business_name: string; location_name: string; platforms: Array<{ name: string; url: string }> };

      const locationLine = data.location_name
        ? `<div class="review-location">${escapeHtml(data.location_name)}</div>`
        : "";

      container.innerHTML = `
        <div class="review-business-header">
          <div class="review-business-name">${escapeHtml(data.business_name)}</div>
          ${locationLine}
        </div>
        <p class="public-lead">Thank you for your visit! We'd love it if you'd take a moment to share your experience.</p>
        <div class="public-platform-links">
          ${data.platforms.map((p) => `
            <a href="${escapeHtml(p.url)}" target="_blank" rel="noreferrer" class="platform-link-btn">
              Leave a review on ${escapeHtml(p.name)}
            </a>
          `).join("")}
        </div>
      `;
    } catch (error) {
      container.innerHTML = `<p class="muted">${escapeHtml(error instanceof Error ? error.message : "Something went wrong.")}</p>`;
    }
  }

  type FeedbackQuestion = { id: string; text: string; type: "rating" | "text" };
  type FeedbackTokenData = { business_name: string; location_name: string; questions: FeedbackQuestion[] };

  function renderFeedbackQuestion(q: FeedbackQuestion): string {
    if (q.type === "rating") {
      const buttons = [1, 2, 3, 4, 5].map((n) => `
        <button type="button" class="rating-btn" data-qid="${escapeHtml(q.id)}" data-val="${n}">${n}</button>
      `).join("");
      return `
        <div class="feedback-question" data-question="${escapeHtml(q.id)}">
          <label class="feedback-question-label">${escapeHtml(q.text)}</label>
          <div class="rating-group" role="group" aria-label="${escapeHtml(q.text)}">
            ${buttons}
          </div>
          <input type="hidden" name="${escapeHtml(q.id)}" value="">
        </div>
      `;
    }
    return `
      <div class="feedback-question" data-question="${escapeHtml(q.id)}">
        <label class="feedback-question-label" for="fq-${escapeHtml(q.id)}">${escapeHtml(q.text)}</label>
        <textarea
          id="fq-${escapeHtml(q.id)}"
          name="${escapeHtml(q.id)}"
          class="feedback-textarea"
          rows="3"
          placeholder="Your answer (optional)"
        ></textarea>
      </div>
    `;
  }

  async function fetchFeedbackToken(apiBaseUrl: string, token: string) {
    const container = mountNode.querySelector<HTMLElement>("#public-content");
    if (!container) return;
    try {
      const resp = await fetch(`${apiBaseUrl}/v1/f/${encodeURIComponent(token)}`);
      if (!resp.ok) throw new Error("Feedback link not found or expired.");
      const data = await resp.json() as FeedbackTokenData;

      const locationLine = data.location_name
        ? `<div class="review-location">${escapeHtml(data.location_name)}</div>`
        : "";

      container.innerHTML = `
        <div class="review-business-header">
          <div class="review-business-name">${escapeHtml(data.business_name)}</div>
          ${locationLine}
        </div>
        <p class="public-lead feedback-intro">We are sorry we did not meet your needs. Thank you for helping us to improve our service.</p>
        <p class="feedback-scale-note">Please rate your service today: <strong>1</strong> – Far Below Expectation &nbsp;·&nbsp; <strong>5</strong> – Well Above Expectation</p>
        <form id="public-feedback-form" class="public-feedback-form">
          ${data.questions.map(renderFeedbackQuestion).join("")}
          <button class="primary-button feedback-submit-btn" type="submit">Submit feedback</button>
        </form>
        <div id="public-feedback-result"></div>
      `;
      bindPublicFeedbackForm(apiBaseUrl, token);
    } catch (error) {
      container.innerHTML = `<p class="muted">${escapeHtml(error instanceof Error ? error.message : "Something went wrong.")}</p>`;
    }
  }

  function bindPublicFeedbackForm(apiBaseUrl: string, token: string) {
    // Wire up rating buttons — clicking selects the value and updates the hidden input.
    mountNode.querySelectorAll<HTMLButtonElement>(".rating-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        const qid = btn.dataset.qid ?? "";
        const val = btn.dataset.val ?? "";
        const group = btn.closest(".feedback-question");
        if (!group) return;
        group.querySelectorAll<HTMLButtonElement>(".rating-btn").forEach((b) => b.classList.remove("selected"));
        btn.classList.add("selected");
        const hidden = group.querySelector<HTMLInputElement>(`input[name="${qid}"]`);
        if (hidden) hidden.value = val;
      });
    });

    mountNode.querySelector<HTMLFormElement>("#public-feedback-form")
      ?.addEventListener("submit", async (event) => {
        event.preventDefault();
        const form = event.currentTarget as HTMLFormElement;
        const formData = new FormData(form);
        const resultEl = mountNode.querySelector<HTMLElement>("#public-feedback-result");
        const btn = form.querySelector<HTMLButtonElement>(".feedback-submit-btn");

        const answers = Array.from(formData.entries())
          .filter(([, v]) => String(v).trim() !== "")
          .map(([k, v]) => ({ question_id: k, answer: String(v).trim() }));

        if (answers.length === 0) {
          if (resultEl) resultEl.innerHTML = `<p class="banner error">Please answer at least one question before submitting.</p>`;
          return;
        }

        if (btn) { btn.disabled = true; btn.textContent = "Sending…"; }

        try {
          const resp = await fetch(`${apiBaseUrl}/v1/f/${encodeURIComponent(token)}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ answers }),
          });
          if (!resp.ok) throw new Error("Submission failed.");
          form.style.display = "none";
          if (resultEl) resultEl.innerHTML = `<p class="public-lead">Thank you for your feedback! We appreciate you taking the time to help us improve.</p>`;
        } catch (error) {
          if (btn) { btn.disabled = false; btn.textContent = "Submit feedback"; }
          if (resultEl) resultEl.innerHTML = `<p class="banner error">${escapeHtml(error instanceof Error ? error.message : "An error occurred.")}</p>`;
        }
      });
  }

  getAuthSnapshot()
    .then(async (snapshot) => {
      console.log(`[auth] initial snapshot — user=${snapshot.user?.email ?? "none"}`);
      state.auth = snapshot;
      render();
      if (snapshot.user) {
        await refreshAppData();
      }
    })
    .catch((error) => {
      console.error("[auth] getAuthSnapshot failed:", error);
      setError(readError(error));
      render();
    });

  onAuthStateChange(async (snapshot) => {
    console.log(`[auth] state change — user=${snapshot.user?.email ?? "none"}`);
    state.auth = snapshot;
    setBusy(false);
    render();
    if (snapshot.user) {
      await refreshAppData();
    }
  });

  render();
}

function renderPublicShell(pathname: string) {
  const isReview = pathname.startsWith("/r/");
  return `
    ${renderDevEnvBar()}
    <div class="public-shell">
      <div class="public-hero">
        <div class="eyebrow">Rate Follow Up</div>
        <h2>${isReview ? "Leave a Review" : "Share Your Feedback"}</h2>
      </div>
      <div class="public-card" id="public-content">
        <p class="muted">Loading…</p>
      </div>
    </div>
  `;
}

function renderDevEnvBar(): string {
  const mode = getAppMode();
  if (mode !== "dev1" && mode !== "dev2") return "";
  return `<div class="dev-env-bar ${mode}">${mode.toUpperCase()} — development instance</div>`;
}

function resolveRoute(pathname: string): RouteName {
  if (pathname === "/profile-select") return "profile-select";
  if (pathname === "/test-accounts") return "test-accounts";
  if (pathname === "/admin") return "admin";
  if (pathname === "/team") return "team";
  const matched = routes.find((route) => route.path === pathname);
  return matched?.name ?? "dashboard";
}

function formatPhoneDisplay(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 11 && digits[0] === "1") {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
}

function formatLogTimestamp(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${mm}/${dd}/${yyyy} ${hh}:${min}`;
}

function renderAuthLanding(state: {
  auth: AuthSnapshot;
  busy: boolean;
  notice: string;
  error: string;
}) {
  return `
    ${renderDevEnvBar()}
    <main class="auth-shell">
      <section class="hero-card">
        <div class="eyebrow">Rate Follow Up</div>
        <h1>Login or create an account</h1>
        <p class="lede">
          Continue with Google, or get a one-time sign-in link sent to your inbox — no password needed.
        </p>
        ${renderBanner(state)}
        ${state.auth.configured ? renderLoginPanel(state) : renderConfigPanel()}
      </section>
    </main>
  `;
}

function renderLoginPanel(state: { busy: boolean }) {
  return `
    <div class="stack">
      <button class="primary-button" data-google-login ${disabledAttr(state.busy)}>
        Continue with Google
      </button>
    </div>
  `;
}

function renderConfigPanel() {
  return `
    <div class="warning-card">
      <h2>Firebase configuration required</h2>
      <p>
        Add the <code>VITE_FIREBASE_*</code> environment variables
        before deploying or running the app.
      </p>
    </div>
  `;
}

// renderAdminShell is deliberately NOT built on renderAuthedApp/renderRouteContent
// — the admin panel (issue #53) must not share the regular sidebar/nav, so an
// admin viewing customer billing data is never one click away from regular
// account-management UI, and so the page is visually unmistakable as a
// different area of the app.
function renderAdminShell(state: {
  auth: AuthSnapshot;
  adminTab: AdminTab;
  adminAccounts: AdminAccountOverview[] | null;
  adminAccountsLoading: boolean;
  adminActivityLog: AdminActivityLogEntry[] | null;
  adminActivityLogLoading: boolean;
  adminActivityLogFilter: string;
  adminPhoneNumbers: AdminPhoneNumberEntry[] | null;
  adminPhoneNumbersLoading: boolean;
  adminPhoneNumbersFilter: string;
  adminOptOuts: AdminOptOutEntry[] | null;
  adminOptOutsLoading: boolean;
  adminTransactions: AdminTransactionsData | null;
  adminTransactionsLoading: boolean;
  adminCustomerStatsAccountId: string | null;
  adminCustomerStatsRange: AdminCustomerStatsRange;
  adminCustomerStats: AdminCustomerStatsData | null;
  adminCustomerStatsLoading: boolean;
  notice: string;
  error: string;
}) {
  const tabs: Array<{ id: AdminTab; label: string }> = [
    { id: "accounts", label: "Accounts" },
    { id: "activity-log", label: "Activity Log" },
    { id: "phone-numbers", label: "Phone Numbers" },
    { id: "opt-outs", label: "Global Opt-Outs" },
    { id: "transactions", label: "Transactions" },
    { id: "customer-stats", label: "Customer Stats" },
  ];

  let content: string;
  switch (state.adminTab) {
    case "activity-log":
      content = renderAdminActivityLogPage(state.adminActivityLog, state.adminActivityLogLoading, state.adminActivityLogFilter);
      break;
    case "phone-numbers":
      content = renderAdminPhoneNumbersPage(state.adminPhoneNumbers, state.adminPhoneNumbersLoading, state.adminPhoneNumbersFilter);
      break;
    case "opt-outs":
      content = renderAdminOptOutsPage(state.adminOptOuts, state.adminOptOutsLoading);
      break;
    case "transactions":
      content = renderAdminTransactionsPage(state.adminTransactions, state.adminTransactionsLoading);
      break;
    case "customer-stats":
      content = renderAdminCustomerStatsPage(
        state.adminAccounts,
        state.adminAccountsLoading,
        state.adminCustomerStatsAccountId,
        state.adminCustomerStatsRange,
        state.adminCustomerStats,
        state.adminCustomerStatsLoading,
      );
      break;
    case "accounts":
    default:
      content = renderAdminAccountsPage(state.adminAccounts, state.adminAccountsLoading);
  }

  return `
    <div class="admin-shell">
      <header class="admin-header">
        <div class="admin-header-title">
          RateFollowUp <span class="admin-header-badge">Admin</span>
        </div>
        <div class="admin-header-actions">
          <span class="admin-header-email">${escapeHtml(state.auth.user?.email ?? "")}</span>
          <a href="/" data-nav class="secondary-button small-button">Exit admin</a>
          <button class="secondary-button small-button" data-sign-out>Sign out</button>
        </div>
      </header>
      <nav class="admin-tabs">
        ${tabs.map((t) => `
          <button
            type="button"
            class="admin-tab ${state.adminTab === t.id ? "active" : ""}"
            data-admin-tab="${t.id}"
          >${t.label}</button>
        `).join("")}
      </nav>
      <main class="admin-content">
        ${renderBanner(state)}
        ${content}
      </main>
    </div>
  `;
}

function renderAuthedApp(state: {
  auth: AuthSnapshot;
  appData: AppData | null;
  route: RouteName;
  busy: boolean;
  loadingData: boolean;
  notice: string;
  error: string;
  csvResult: CsvValidationResult | null;
  csvFileName: string;
  lastSavedUploadId: string;
  selectedUploadDetails: UploadDetails | null;
  uploadAttested: boolean;
  uploadMode: "file" | "manual";
  billingShowUpdateForm: boolean;
  billingSelectedPlanId: string | null;
  billingConfirmChecked: boolean;
  billingDemoConfirmChecked: boolean;
  billingModalOpen: "plan" | "demo" | "cancel" | null;
  pendingPlatforms: ReviewPlatform[];
  sendStatus: "idle" | "sending" | "complete" | "failed" | "stopped";
  sendResult: SendResult | null;
  selectedProfileId: string | null;
  phoneLog: PhoneLogEntry[] | null;
  phoneLogLoading: boolean;
  logPhoneFilter: string;
  numberHistory: NumberHistoryEntry[] | null;
  numberHistoryLoading: boolean;
  numberHistoryFilter: string;
  isTesting: boolean;
  activeAccountIsTesting: boolean;
  testingAccounts: TestingAccount[] | null;
  testingAccountsLoading: boolean;
  adminAccounts: AdminAccountOverview[] | null;
  adminAccountsLoading: boolean;
  authorizedUsers: AccountAccess[] | null;
  authorizedUsersLoading: boolean;
  linkedAccounts: AccountAccess[] | null;
  guestAccountId: string | null;
  guestOwnerEmail: string | null;
  showClosedBatches: boolean;
  dashboardBatchId: string;
}) {
  const profile = state.appData?.profile;
  const profileLabel = profile
    ? escapeHtml(profile.location_name || profile.business_name || "Location")
    : "";

  return `
    ${renderDevEnvBar()}
    <div class="app-shell">
      <aside class="sidebar">
        <div class="sidebar-top">
          <div class="sidebar-brand-row">
            <div class="sidebar-brand">Rate Follow Up</div>
            ${state.appData?.account != null ? `
              <div class="sidebar-token-badge" title="Token balance">
                ${formatNumber(state.appData.account.token_balance)} <span class="sidebar-token-label">tokens</span>
              </div>
            ` : ""}
          </div>
          <div class="account-label">${escapeHtml(state.auth.user?.email ?? "")}</div>
          ${profileLabel ? `
            <div class="profile-label">${profileLabel}</div>
            <button class="sidebar-switch-btn" data-switch-profile ${disabledAttr(state.busy)}>
              Switch location
            </button>
          ` : ""}
          <button class="sidebar-signout-btn" data-sign-out>
            Sign out
          </button>
        </div>
        <nav class="nav-list">
          ${routes
            .filter((route) => {
              if (route.name === "team" && state.guestAccountId) return false;
              return true;
            })
            .map(
              (route) => `
                <a
                  href="${route.path}"
                  data-nav
                  class="nav-link ${route.name === state.route ? "active" : ""}"
                >
                  ${route.label}
                </a>
              `,
            )
            .join("")}
          ${state.isTesting ? `
            <a
              href="/test-accounts"
              data-nav
              class="nav-link nav-link-testing ${state.route === "test-accounts" ? "active" : ""}"
            >
              Test accounts
            </a>
          ` : ""}
          ${isAdminEmail(state.auth.user?.email) ? `
            <a
              href="/admin"
              data-nav
              class="nav-link nav-link-testing ${state.route === "admin" ? "active" : ""}"
            >
              Admin
            </a>
          ` : ""}
        </nav>
        ${state.guestAccountId ? `
          <div class="guest-account-banner">
            <div class="guest-account-label">Viewing as</div>
            <div class="guest-account-email">${escapeHtml(state.guestOwnerEmail ?? "")}</div>
            <button class="secondary-button small-button" data-switch-own-account>Switch back</button>
          </div>
        ` : (state.linkedAccounts && state.linkedAccounts.length > 0) ? `
          <div class="linked-accounts-section">
            <div class="linked-accounts-label">Linked accounts</div>
            ${state.linkedAccounts.map((a, i) => `
              <button class="linked-account-btn" data-switch-guest-account="${i}">
                ${escapeHtml(a.owner_email)}
              </button>
            `).join("")}
          </div>
        ` : ""}
      </aside>
      <main class="content-shell">
        ${renderBanner(state)}
        ${state.loadingData ? renderLoadingPanel() : renderRouteContent({
          appData: state.appData,
          route: state.route,
          csvResult: state.csvResult,
          csvFileName: state.csvFileName,
          lastSavedUploadId: state.lastSavedUploadId,
          selectedUploadDetails: state.selectedUploadDetails,
          uploadAttested: state.uploadAttested,
          uploadMode: state.uploadMode,
          billingShowUpdateForm: state.billingShowUpdateForm,
          billingSelectedPlanId: state.billingSelectedPlanId,
          billingConfirmChecked: state.billingConfirmChecked,
          billingDemoConfirmChecked: state.billingDemoConfirmChecked,
          billingModalOpen: state.billingModalOpen,
          pendingPlatforms: state.pendingPlatforms,
          sendStatus: state.sendStatus,
          sendResult: state.sendResult,
          phoneLog: state.phoneLog,
          phoneLogLoading: state.phoneLogLoading,
          logPhoneFilter: state.logPhoneFilter,
          numberHistory: state.numberHistory,
          numberHistoryLoading: state.numberHistoryLoading,
          numberHistoryFilter: state.numberHistoryFilter,
          isTesting: state.isTesting,
          activeAccountIsTesting: state.activeAccountIsTesting,
          testingAccounts: state.testingAccounts,
          testingAccountsLoading: state.testingAccountsLoading,
          adminAccounts: state.adminAccounts,
          adminAccountsLoading: state.adminAccountsLoading,
          authorizedUsers: state.authorizedUsers,
          authorizedUsersLoading: state.authorizedUsersLoading,
          guestAccountId: state.guestAccountId,
          busy: state.busy,
          showClosedBatches: state.showClosedBatches,
          dashboardBatchId: state.dashboardBatchId,
          currentUserEmail: state.auth.user?.email ?? undefined,
        })}
      </main>
    </div>
  `;
}

function renderRouteContent(state: {
  appData: AppData | null;
  route: RouteName;
  csvResult: CsvValidationResult | null;
  csvFileName: string;
  lastSavedUploadId: string;
  selectedUploadDetails: UploadDetails | null;
  uploadAttested: boolean;
  uploadMode: "file" | "manual";
  billingShowUpdateForm: boolean;
  billingSelectedPlanId: string | null;
  billingConfirmChecked: boolean;
  billingDemoConfirmChecked: boolean;
  billingModalOpen: "plan" | "demo" | "cancel" | null;
  pendingPlatforms: ReviewPlatform[];
  sendStatus: "idle" | "sending" | "complete" | "failed" | "stopped";
  sendResult: SendResult | null;
  phoneLog: PhoneLogEntry[] | null;
  phoneLogLoading: boolean;
  logPhoneFilter: string;
  numberHistory: NumberHistoryEntry[] | null;
  numberHistoryLoading: boolean;
  numberHistoryFilter: string;
  isTesting: boolean;
  activeAccountIsTesting: boolean;
  testingAccounts: TestingAccount[] | null;
  testingAccountsLoading: boolean;
  adminAccounts: AdminAccountOverview[] | null;
  adminAccountsLoading: boolean;
  authorizedUsers: AccountAccess[] | null;
  authorizedUsersLoading: boolean;
  guestAccountId: string | null;
  busy: boolean;
  showClosedBatches: boolean;
  dashboardBatchId: string;
  currentUserEmail: string | undefined;
}) {
  switch (state.route) {
    case "profile-select":
      return renderProfileSelectPage(state.appData);
    case "profile":
      return renderProfilePage(state.appData, state.pendingPlatforms, state.isTesting);
    case "upload":
      return renderUploadPage(
        state.appData,
        state.csvResult,
        state.csvFileName,
        state.lastSavedUploadId,
        state.selectedUploadDetails,
        state.uploadAttested,
        state.uploadMode,
        state.sendStatus,
        state.sendResult,
        state.activeAccountIsTesting,
        state.showClosedBatches,
      );
    case "billing":
      return renderBillingPage(
        state.appData,
        state.billingShowUpdateForm,
        Boolean(state.guestAccountId),
        state.activeAccountIsTesting,
        state.billingSelectedPlanId,
        state.billingConfirmChecked,
        state.billingDemoConfirmChecked,
        state.billingModalOpen,
      );
    case "log":
      return renderLogPage(state.appData, state.phoneLog, state.phoneLogLoading, state.logPhoneFilter);
    case "numbers":
      return renderNumberHistoryPage(state.appData, state.numberHistory, state.numberHistoryLoading, state.numberHistoryFilter);
    case "team":
      return state.guestAccountId
        ? renderWelcomePage(state.appData, state.sendStatus, state.sendResult, state.activeAccountIsTesting, state.dashboardBatchId)
        : renderTeamPage(state.authorizedUsers, state.authorizedUsersLoading, state.busy);
    case "test-accounts":
      return state.isTesting
        ? renderTestAccountsPage(state.testingAccounts, state.testingAccountsLoading, state.currentUserEmail)
        : renderWelcomePage(state.appData, state.sendStatus, state.sendResult, state.activeAccountIsTesting, state.dashboardBatchId);
    case "admin":
      return isAdminEmail(state.currentUserEmail)
        ? renderAdminAccountsPage(state.adminAccounts, state.adminAccountsLoading)
        : renderWelcomePage(state.appData, state.sendStatus, state.sendResult, state.activeAccountIsTesting, state.dashboardBatchId);
    case "dashboard":
    default:
      return renderWelcomePage(state.appData, state.sendStatus, state.sendResult, state.activeAccountIsTesting, state.dashboardBatchId);
  }
}

type SendResult = { total: number; enqueued: number; skipped: number; failed: number; results: Array<{ phone: string; status: string; reason?: string }> };

function formatSendResultStatus(status: string): string {
  switch (status) {
    case "enqueued": return "Queued for sending";
    case "sent": return "Sent";
    case "failed": return "Failed to queue";
    case "skipped_opted_out": return "Skipped — opted out";
    case "skipped_global_optout": return "Skipped — opted out globally";
    case "skipped_recent": return "Skipped — contacted within 1 year";
    case "no_phone": return "Skipped — no valid number";
    default: return status;
  }
}

function renderSendResultsDropdown(sendResult: SendResult) {
  if (!sendResult.results || sendResult.results.length === 0) return "";

  const rows = sendResult.results.map((r) => {
    const isSent = r.status === "enqueued" || r.status === "sent";
    const isFailure = r.status === "failed";
    const label = formatSendResultStatus(r.status);
    const reasonHtml = r.reason
      ? `<div class="send-result-reason">${escapeHtml(r.reason)}</div>`
      : "";
    return `
      <div class="send-result-row ${isSent ? "result-sent" : isFailure ? "result-failed" : "result-skipped"}">
        <div class="send-result-main">
          <span class="send-result-phone">${escapeHtml(r.phone || "Unknown")}</span>
          <span class="send-result-label">${label}</span>
        </div>
        ${reasonHtml}
      </div>
    `;
  }).join("");

  const summary = sendResult.failed > 0
    ? `${sendResult.enqueued} queued, ${sendResult.failed} failed — see details`
    : `All ${sendResult.enqueued} queued for sending`;

  return `
    <details class="send-results-details">
      <summary class="send-results-summary">${summary}</summary>
      <div class="send-results-list">${rows}</div>
    </details>
  `;
}

function renderSendStatusBadge(
  sendStatus: "idle" | "sending" | "complete" | "failed" | "stopped",
  sendResult: SendResult | null,
) {
  if (sendStatus === "idle") return "";
  if (sendStatus === "sending") {
    return `<div class="send-status-badge status-sending">⏳ Queuing messages…</div>`;
  }
  if (sendStatus === "complete" && sendResult) {
    const msg = sendResult.failed > 0
      ? `✓ ${sendResult.enqueued} queued, ${sendResult.failed} failed`
      : `✓ ${sendResult.enqueued} message${sendResult.enqueued === 1 ? "" : "s"} queued for sending`;
    return `
      <div class="send-status-badge status-complete">${msg}</div>
      <div class="send-status-note">Messages send in the background — this batch updates as they go out.</div>
      ${renderSendResultsDropdown(sendResult)}
    `;
  }
  if (sendStatus === "failed") {
    return `<div class="send-status-badge status-failed">✗ Could not queue — check error above</div>`;
  }
  if (sendStatus === "stopped") {
    return `<div class="send-status-badge status-stopped">⊘ Send cancelled</div>`;
  }
  return "";
}

function renderLogPage(
  appData: AppData | null,
  phoneLog: PhoneLogEntry[] | null,
  loading: boolean,
  phoneFilter = "",
): string {
  const profile = appData?.profile;
  if (!profile) {
    return `<div class="page-section"><p class="muted">Select a location to view its activity log.</p></div>`;
  }

  if (loading) {
    return `<div class="page-section"><p class="muted">Loading activity log…</p></div>`;
  }

  if (!phoneLog) {
    return `<div class="page-section"><p class="muted">Navigate to this page to load the activity log.</p></div>`;
  }

  if (phoneLog.length === 0) {
    return `
      <div class="page-section">
        <h2 class="page-title">Activity Log</h2>
        <p class="muted">No activity yet for ${escapeHtml(profile.location_name || profile.business_name)}.</p>
      </div>
    `;
  }

  const eventLabel: Record<string, string> = {
    loaded: "Loaded into the system",
    rejected: "Rejected",
    suppressed: "Suppressed",
    sent: "Sent opt-in message",
    send_failed: "Send failed",
    skipped: "Skipped",
    recv_yes: "Replied YES",
    recv_no: "Replied NO",
    opted_out: "Opted out",
    clicked_review: "Clicked review link",
    feedback: "Submitted feedback",
  };

  const eventClass: Record<string, string> = {
    loaded: "log-event-loaded",
    rejected: "log-event-rejected",
    suppressed: "log-event-suppressed",
    sent: "log-event-sent",
    send_failed: "log-event-failed",
    skipped: "log-event-skipped",
    recv_yes: "log-event-yes",
    recv_no: "log-event-no",
    opted_out: "log-event-opted-out",
    clicked_review: "log-event-yes",
    feedback: "log-event-yes",
  };

  // Build the per-number filter options: one entry per distinct number,
  // grouped by digits so the same number in different formats collapses to one.
  const digitsKey = (phone: string) => phone.replace(/\D/g, "");
  const numbers = new Map<string, { display: string; count: number }>();
  for (const entry of phoneLog) {
    const key = digitsKey(entry.phone);
    if (!key) continue;
    const existing = numbers.get(key);
    if (existing) existing.count += 1;
    else numbers.set(key, { display: formatPhoneDisplay(entry.phone), count: 1 });
  }
  const sortedNumbers = [...numbers.entries()].sort((a, b) => a[1].display.localeCompare(b[1].display));

  // Apply the active filter (client-side; the full log is already loaded).
  const activeFilter = numbers.has(phoneFilter) ? phoneFilter : "";
  const visible = activeFilter ? phoneLog.filter((e) => digitsKey(e.phone) === activeFilter) : phoneLog;

  const options = [
    `<option value=""${activeFilter === "" ? " selected" : ""}>All numbers (${numbers.size})</option>`,
    ...sortedNumbers.map(
      ([key, { display, count }]) =>
        `<option value="${escapeHtml(key)}"${key === activeFilter ? " selected" : ""}>${escapeHtml(display)} (${count})</option>`,
    ),
  ].join("");

  const rows = visible.map((entry) => {
    const ts = formatLogTimestamp(entry.timestamp);
    const phone = formatPhoneDisplay(entry.phone);
    const label = eventLabel[entry.event] ?? entry.event;
    const cls = eventClass[entry.event] ?? "";
    return `
      <tr class="log-row ${cls}">
        <td class="log-ts">${escapeHtml(ts)}</td>
        <td class="log-phone">${escapeHtml(phone)}</td>
        <td class="log-detail">${escapeHtml(entry.detail || label)}</td>
      </tr>
    `;
  }).join("");

  const countLabel = activeFilter
    ? `${visible.length} event${visible.length !== 1 ? "s" : ""} for ${escapeHtml(numbers.get(activeFilter)!.display)} — newest first`
    : `${phoneLog.length} event${phoneLog.length !== 1 ? "s" : ""} — newest first`;

  return `
    <div class="page-section">
      <h2 class="page-title">Activity Log</h2>
      <div class="log-toolbar">
        <label class="log-filter">
          <span class="log-filter-label">Number</span>
          <select class="text-input" data-log-phone-filter>${options}</select>
        </label>
        <p class="muted log-count">${countLabel}</p>
      </div>
      <div class="log-table-wrap">
        <table class="log-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Phone</th>
              <th>Event</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    </div>
  `;
}

// canonicalConvState maps legacy / row-style state values (from pre-#34
// conversation docs) onto the current state-machine states so the label
// functions have a single vocabulary to switch on.
function canonicalConvState(state: string): string {
  switch (state) {
    case "queued": return "waiting_opt_in_1";
    case "optin_1_sent": return "waiting_opt_in_2";
    case "optin_sent":
    case "sent": return "waiting_response";
    case "completed_yes": return "sent_review_link";
    case "completed_no": return "sent_feedback_link";
    case "failed": return "send_error";
    default: return state;
  }
}

// numberInteraction renders the ordered "what happened" summary for one number.
// The conversation flow is linear, so the current state implies every prior step.
function numberInteraction(entry: NumberHistoryEntry): string {
  if (entry.state) {
    const optin = "Opt-in 1 sent; Opt-in 2 sent";
    switch (canonicalConvState(entry.state)) {
      case "waiting_opt_in_1": return "Queued — not yet sent";
      case "waiting_opt_in_2": return "Opt-in 1 sent";
      case "waiting_response": return `${optin}; awaiting reply`;
      case "sending_review_link": return `${optin}; Replied YES`;
      case "sent_review_link": return `${optin}; Replied YES; Review link sent`;
      case "clicked_review": return `${optin}; Replied YES; Review link sent; Review link clicked`;
      case "sending_feedback_link": return `${optin}; Replied NO`;
      case "sent_feedback_link": return `${optin}; Replied NO; Feedback form sent`;
      case "clicked_form": return `${optin}; Replied NO; Feedback form sent; Form clicked`;
      case "form_submitted": return `${optin}; Replied NO; Feedback form sent; Form clicked; Form submitted`;
      case "opted_out": return "Opted out (replied STOP)";
      case "send_error": return "Send error — message failed";
      default: return entry.state;
    }
  }
  switch (entry.rowStatus) {
    case "rejected_invalid":
    case "no_phone": return "Did not send — invalid number";
    case "rejected_duplicate": return "Did not send — duplicate number";
    case "rejected_suppressed":
    case "skipped_recent": return "Did not send — contacted within the last year";
    case "skipped_opted_out": return "Did not send — previously opted out";
    case "rejected_global_optout":
    case "skipped_global_optout": return "Did not send — opted out of messages from all businesses";
    case "queued":
    case "valid": return "Loaded — not yet sent";
    default: return entry.rowStatus ? `Loaded — ${entry.rowStatus}` : "Loaded";
  }
}

// numberOutcome is the short at-a-glance status chip.
function numberOutcome(entry: NumberHistoryEntry): { label: string; cls: string } {
  if (entry.state) {
    switch (canonicalConvState(entry.state)) {
      case "clicked_review":
      case "form_submitted": return { label: "Completed", cls: "nh-good" };
      case "clicked_form": return { label: "Opened form", cls: "nh-warn" };
      case "sent_review_link": return { label: "Review link sent", cls: "nh-warn" };
      case "sent_feedback_link": return { label: "Feedback form sent", cls: "nh-warn" };
      case "sending_review_link":
      case "sending_feedback_link": return { label: "Sending link", cls: "nh-warn" };
      case "waiting_response": return { label: "Awaiting reply", cls: "nh-warn" };
      case "waiting_opt_in_1":
      case "waiting_opt_in_2": return { label: "Sending opt-in", cls: "nh-neutral" };
      case "opted_out": return { label: "Opted out", cls: "nh-skip" };
      case "send_error": return { label: "Send failed", cls: "nh-bad" };
      default: return { label: entry.state, cls: "nh-neutral" };
    }
  }
  switch (entry.rowStatus) {
    case "rejected_invalid":
    case "no_phone": return { label: "Invalid — not sent", cls: "nh-bad" };
    case "rejected_duplicate": return { label: "Duplicate — not sent", cls: "nh-skip" };
    case "rejected_suppressed":
    case "skipped_recent": return { label: "Skipped — recent", cls: "nh-skip" };
    case "skipped_opted_out": return { label: "Opted out", cls: "nh-skip" };
    case "rejected_global_optout":
    case "skipped_global_optout": return { label: "Opted out (global)", cls: "nh-skip" };
    case "queued":
    case "valid": return { label: "Not yet sent", cls: "nh-neutral" };
    default: return { label: entry.rowStatus ?? "—", cls: "nh-neutral" };
  }
}

function renderNumberHistoryPage(
  appData: AppData | null,
  history: NumberHistoryEntry[] | null,
  loading: boolean,
  filter = "",
): string {
  const profile = appData?.profile;
  if (!profile) {
    return `<div class="page-section"><p class="muted">Select a location to view its number history.</p></div>`;
  }
  if (loading) {
    return `<div class="page-section"><p class="muted">Loading number history…</p></div>`;
  }
  if (!history) {
    return `<div class="page-section"><p class="muted">Navigate to this page to load the number history.</p></div>`;
  }
  if (history.length === 0) {
    return `
      <div class="page-section">
        <h2 class="page-title">Number History</h2>
        <p class="muted">No numbers yet for ${escapeHtml(profile.location_name || profile.business_name)}.</p>
      </div>
    `;
  }

  const q = filter.trim().replace(/\D/g, "");
  const visible = q ? history.filter((e) => e.phone.includes(q)) : history;

  const rows = visible.map((entry) => {
    const outcome = numberOutcome(entry);
    const batchBadge = entry.batchCount > 1 ? `<span class="nh-batches" title="Appears in ${entry.batchCount} batches">×${entry.batchCount}</span>` : "";
    return `
      <tr>
        <td class="nh-number">
          <button type="button" class="nh-number-link" data-view-number="${escapeHtml(entry.phone)}" title="View this number in the Activity Log">${escapeHtml(formatPhoneDisplay(entry.display))}</button>
          ${batchBadge}
        </td>
        <td class="nh-date">${entry.dateLoaded ? escapeHtml(formatLogTimestamp(entry.dateLoaded)) : "—"}</td>
        <td class="nh-date">${entry.dateSent ? escapeHtml(formatLogTimestamp(entry.dateSent)) : "—"}</td>
        <td class="nh-interaction">${escapeHtml(numberInteraction(entry))}</td>
        <td><span class="nh-chip ${outcome.cls}">${escapeHtml(outcome.label)}</span></td>
      </tr>
    `;
  }).join("");

  const countLabel = q
    ? `${visible.length} of ${history.length} number${history.length !== 1 ? "s" : ""}`
    : `${history.length} number${history.length !== 1 ? "s" : ""} — most recent activity first`;

  return `
    <div class="page-section">
      <h2 class="page-title">Number History</h2>
      <div class="log-toolbar">
        <label class="log-filter">
          <span class="log-filter-label">Search</span>
          <input type="tel" class="text-input" data-number-search placeholder="Type a number…" value="${escapeHtml(filter)}" />
        </label>
        <p class="muted log-count">${countLabel}</p>
      </div>
      <div class="log-table-wrap">
        <table class="log-table nh-table">
          <thead>
            <tr>
              <th>Number</th>
              <th>Loaded</th>
              <th>Sent</th>
              <th>Interaction</th>
              <th>Outcome</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    </div>
  `;
}

// getOnboardingStatus is the single source of truth for "is this account done
// setting up" — used both by renderWelcomePage's step cards and by
// handleProfileSave/handleConfirmPurchase to decide whether to railroad the
// user back to the dashboard after saving (issue #63), so the two places
// can't drift out of sync on what "done" means.
function getOnboardingStatus(appData: AppData | null | undefined, isTesting: boolean) {
  const account = appData?.account;
  const profile = appData?.profile;
  const uploads = appData?.uploads ?? [];
  const activeUploads = uploads.filter((u) => u.status !== "closed");

  const profileDone = Boolean(profile?.is_profile_complete);
  const uploadDone = activeUploads.length > 0;
  const billingDone = isTesting ? true : Boolean(account?.square_card_id);
  const billingStatus = account?.billing_status ?? "not_configured";
  const billingWarning = billingStatus === "past_due" || billingStatus === "canceled";
  const readyToSendCount = activeUploads
    .filter((u) => u.status === "validated")
    .reduce((sum, u) => sum + u.valid_rows, 0);
  const totalQueued = activeUploads.reduce((sum, u) => sum + u.queued_rows, 0);
  const allDone = profileDone && billingDone && !billingWarning && uploadDone && readyToSendCount === 0;

  return { activeUploads, profileDone, uploadDone, billingDone, billingStatus, billingWarning, readyToSendCount, totalQueued, allDone };
}

function renderWelcomePage(
  appData: AppData | null,
  sendStatus: "idle" | "sending" | "complete" | "failed" | "stopped" = "idle",
  sendResult: SendResult | null = null,
  isTesting = false,
  dashboardBatchId = "all",
) {
  const account = appData?.account;
  const uploads = appData?.uploads ?? [];

  const {
    activeUploads,
    profileDone,
    uploadDone,
    billingDone,
    billingStatus,
    billingWarning,
    readyToSendCount,
    totalQueued,
    allDone,
  } = getOnboardingStatus(appData, isTesting);
  const profile = appData?.profile;
  const selectedPlan = getUsagePlan(account?.usage_plan_id);

  const profileChecks = buildProfileChecks(profile);

  const step1 = profileDone
    ? renderStepDone(1, "Profile complete")
    : renderStepActive(1, "Complete your profile",
        `<p>Fill in your business details so Rate Follow Up knows where to send review replies.</p>
         ${renderMiniChecklist(profileChecks)}
         <a href="/profile" data-nav class="primary-button step-cta">Complete profile →</a>`,
      );

  const step2 = isTesting
    ? renderStepDone(2, "Billing — using test tokens")
    : !profileDone
    ? renderStepUpcoming(2, "Set up billing")
    : billingWarning
    ? renderStepWarning(2, "Billing issue",
        `<p>There's a problem with your payment method: <strong>${formatBillingStatus(billingStatus)}</strong>. Messages cannot be sent until this is resolved.</p>
         <a href="/billing" data-nav class="primary-button step-cta">Fix billing →</a>`,
      )
    : billingDone
    ? renderStepDone(2, "Billing set up")
    : renderStepActive(2, "Set up billing",
        `<p>Add a payment method to activate your plan and start sending messages.</p>
         <a href="/billing" data-nav class="primary-button step-cta">Set up billing →</a>`,
      );

  const step3 = !profileDone || !billingDone
    ? renderStepUpcoming(3, "Upload phone numbers")
    : uploadDone
    ? renderStepDone(3, `${activeUploads.length} batch${activeUploads.length === 1 ? "" : "es"} uploaded`)
    : renderStepActive(3, "Upload phone numbers",
        `<p>Upload a CSV, Excel, or text file of customer phone numbers to get started.</p>
         <a href="/upload" data-nav class="primary-button step-cta">Upload numbers →</a>`,
      );

  const step4 = !profileDone || !uploadDone || !billingDone
    ? renderStepUpcoming(4, "Send messages")
    : readyToSendCount > 0
    ? renderStepActive(4, "Send review requests",
        `<p class="send-ready-summary"><strong>${formatNumber(readyToSendCount)}</strong> number${readyToSendCount === 1 ? "" : "s"} ready to send.${sendResult && sendResult.enqueued > 0 ? ` ${formatNumber(sendResult.enqueued)} queued for delivery.` : ""}</p>
         <p>Numbers recently contacted are automatically skipped as repeats.</p>
         ${renderSendStatusBadge(sendStatus, sendResult)}
         <div class="welcome-actions">
           <button class="primary-button step-cta" data-send-all ${sendStatus === "sending" ? "disabled" : ""}>${sendStatus === "sending" ? "Starting…" : "Start sending"}</button>
           ${sendStatus === "sending" ? `<button class="secondary-button welcome-action" data-stop-send>Stop</button>` : `<a href="/upload" data-nav class="secondary-button welcome-action">Review batches</a>`}
         </div>`,
      )
    : renderStepDone(4, `${formatNumber(totalQueued)} message${totalQueued === 1 ? "" : "s"} sent`);

  const recentSentBatch = uploads
    .filter((u) => (u.status === "ready" || u.status === "closed") && u.started_at)
    .sort((a, b) => (b.started_at ?? "").localeCompare(a.started_at ?? ""))[0] ?? null;

  const activitySummary = uploadDone && profileDone
    ? renderActivitySummary(account, uploads, activeUploads, totalQueued, appData?.analytics ?? null, dashboardBatchId, recentSentBatch)
    : "";

  return `
    <div class="dashboard-hero">
      <h1 class="dashboard-welcome">Welcome to Rate Follow Up</h1>
      <div class="dashboard-divider"></div>
      <div class="dashboard-title-row">
        <h2 class="dashboard-title">Dashboard</h2>
        <span class="plan-pill step-pill">✓ Signed in</span>
        <span class="plan-pill">◆ ${escapeHtml(selectedPlan.label)}</span>
        ${allDone ? `<div class="steps-complete-bar">
          <a href="/profile" data-nav class="step-pill">✓ Profile</a>
          <span class="step-pill-sep">›</span>
          <a href="/billing" data-nav class="step-pill">✓ Billing</a>
          <span class="step-pill-sep">›</span>
          <a href="/upload" data-nav class="step-pill">✓ Uploads</a>
          <span class="step-pill-sep">›</span>
          <span class="step-pill">✓ ${totalQueued > 0 ? formatNumber(totalQueued) + " sent" : "Sending"}</span>
        </div>` : ""}
      </div>
    </div>
    ${allDone
      ? ""
      : `<div class="onboarding-flow">
          ${step1}
          ${step2}
          ${step3}
          ${step4}
        </div>`}
    ${activitySummary}
  `;
}

function buildProfileChecks(profile: BusinessProfile | null | undefined) {
  return [
    { label: "Business name", done: Boolean(profile?.business_name?.trim()) },
    { label: "Business address", done: Boolean(profile?.business_address?.trim()) },
    { label: "Business phone", done: Boolean(profile?.business_phone?.trim()) },
    { label: "Contact name", done: Boolean(profile?.contact_name?.trim()) },
    { label: "Contact email", done: Boolean(profile?.primary_email?.trim()) },
    {
      label: "At least one review platform URL",
      done: Boolean(profile?.review_platforms?.some((p) => p.enabled && p.url.trim())),
    },
  ];
}

function renderMiniChecklist(checks: Array<{ label: string; done: boolean }>) {
  return `
    <ul class="profile-checklist">
      ${checks
        .map(
          (c) => `
            <li class="checklist-item ${c.done ? "done" : "todo"}">
              <span class="check-icon">${c.done ? "✓" : "✗"}</span>
              ${escapeHtml(c.label)}
            </li>
          `,
        )
        .join("")}
    </ul>
  `;
}

function renderStepActive(num: number, title: string, body: string) {
  return `
    <div class="step-card step-active">
      <div class="step-header">
        <span class="step-badge badge-active">${num}</span>
        <h3 class="step-title">${escapeHtml(title)}</h3>
      </div>
      <div class="step-body">${body}</div>
    </div>
  `;
}

function renderStepWarning(num: number, title: string, body: string) {
  return `
    <div class="step-card step-warning">
      <div class="step-header">
        <span class="step-badge badge-warning">!</span>
        <h3 class="step-title">${escapeHtml(title)}</h3>
      </div>
      <div class="step-body">${body}</div>
    </div>
  `;
}

function renderStepDone(num: number, label: string) {
  return `
    <div class="step-card step-done">
      <div class="step-header">
        <span class="step-badge badge-done">✓</span>
        <span class="step-done-label">${num}. ${escapeHtml(label)}</span>
      </div>
    </div>
  `;
}

function renderStepUpcoming(num: number, label: string) {
  return `
    <div class="step-card step-upcoming">
      <div class="step-header">
        <span class="step-badge badge-upcoming">${num}</span>
        <span class="step-upcoming-label">${escapeHtml(label)}</span>
      </div>
    </div>
  `;
}

function renderActivitySummary(
  account: BusinessAccount | null | undefined,
  uploads: UploadRecord[],
  activeUploads: UploadRecord[],
  totalQueued: number,
  analytics: ProfileAnalytics | null,
  dashboardBatchId: string,
  recentSentBatch: UploadRecord | null,
) {
  const selectedBatch = dashboardBatchId !== "all"
    ? uploads.find((u) => u.id === dashboardBatchId) ?? null
    : null;

  const batchSelector = uploads.length > 1 ? `
    <div class="batch-selector-row">
      <label class="batch-selector-label">Viewing:</label>
      <select class="batch-selector" data-dashboard-batch-select>
        <option value="all" ${dashboardBatchId === "all" ? "selected" : ""}>All batches</option>
        ${uploads
          .filter((u) => u.status !== "closed")
          .map((u) => `<option value="${escapeHtml(u.id)}" ${dashboardBatchId === u.id ? "selected" : ""}>${escapeHtml(u.batch_name || u.original_filename)}</option>`)
          .join("")}
      </select>
    </div>
  ` : "";

  const recentBatchCard = recentSentBatch && !selectedBatch ? `
    <div class="recent-batch-card">
      <div class="recent-batch-heading">Most recent batch: <strong>${escapeHtml(recentSentBatch.batch_name || recentSentBatch.original_filename)}</strong></div>
      <div class="recent-batch-stats">
        <span>${formatNumber(recentSentBatch.queued_rows)} sent</span>
        <span>${formatNumber(recentSentBatch.suppressed_rows)} repeats</span>
        <span>${formatNumber(recentSentBatch.rejected_rows)} failed</span>
      </div>
      <a href="/upload" data-nav class="secondary-button small-button">Go to batches →</a>
    </div>
  ` : "";

  const analyticsSection = selectedBatch
    ? renderBatchStats(selectedBatch)
    : analytics ? renderAnalyticsSection(analytics) : "";

  return `
    ${batchSelector}
    <p class="activity-summary-line">${activeUploads.length} active batch${activeUploads.length === 1 ? "" : "es"} · ${formatNumber(totalQueued)} sent</p>
    ${recentBatchCard}
    ${analyticsSection}
  `;
}

function renderBatchStats(upload: UploadRecord) {
  return `
    <section class="data-split">
      <div class="data-col">
        <div class="data-col-heading">Batch: ${escapeHtml(upload.batch_name || upload.original_filename)}</div>
        <div class="data-row"><span class="data-row-label">Valid rows</span><span class="data-row-value">${formatNumber(upload.valid_rows)}</span></div>
        <div class="data-row"><span class="data-row-label">Sent</span><span class="data-row-value">${formatNumber(upload.queued_rows)}</span></div>
        <div class="data-row"><span class="data-row-label">Failed</span><span class="data-row-value">${formatNumber(upload.rejected_rows)}</span></div>
        <div class="data-row"><span class="data-row-label">Repeats</span><span class="data-row-value">${formatNumber(upload.suppressed_rows)}</span></div>
        <div class="data-row"><span class="data-row-label">Status</span><span class="data-row-value">${escapeHtml(formatUploadStatus(upload.status))}</span></div>
      </div>
    </section>
  `;
}

function renderAnalyticsSection(a: ProfileAnalytics) {
  return `
    <section class="data-split">
      <div class="data-col">
        <div class="data-col-heading">Engagement</div>
        <div class="data-row"><span class="data-row-label">In process</span><span class="data-row-value">${formatNumber(a.in_process)}</span></div>
        <div class="data-row"><span class="data-row-label">Replied yes</span><span class="data-row-value">${formatNumber(a.replied_yes)}</span></div>
        <div class="data-row"><span class="data-row-label">Replied no</span><span class="data-row-value">${formatNumber(a.replied_no)}</span></div>
        <div class="data-row"><span class="data-row-label">Opted out</span><span class="data-row-value">${formatNumber(a.opted_out)}</span></div>
        <div class="data-row"><span class="data-row-label">Total contacts</span><span class="data-row-value">${formatNumber(a.total)}</span></div>
      </div>
      <div class="data-col">
        <div class="data-col-heading">Outcomes</div>
        <div class="data-row"><span class="data-row-label">Clicked review link</span><span class="data-row-value">${formatNumber(a.clicked_review)}</span></div>
        <div class="data-row"><span class="data-row-label">Clicked feedback form</span><span class="data-row-value">${formatNumber(a.clicked_form)}</span></div>
        <div class="data-row"><span class="data-row-label">Submitted feedback</span><span class="data-row-value">${formatNumber(a.submitted_feedback)}</span></div>
        <div class="data-row"><span class="data-row-label">Repeats (recent contact)</span><span class="data-row-value">${formatNumber(a.skipped)}</span></div>
      </div>
    </section>
  `;
}

function renderPlatformEditorRows(platforms: ReviewPlatform[]) {
  if (platforms.length === 0) {
    return `<p class="muted">No review platforms added yet.</p>`;
  }
  return platforms.map((p, i) => `
    <div class="platform-row">
      <label class="platform-toggle" title="${p.enabled ? "Enabled" : "Disabled"}">
        <input type="checkbox" data-toggle-platform="${i}" ${p.enabled ? "checked" : ""} />
      </label>
      ${p.id === "google" || p.id === "facebook"
        ? `<span class="platform-name-fixed">${escapeHtml(p.name)}</span>`
        : `<input class="platform-name-input" type="text" placeholder="Platform name" value="${escapeHtml(p.name)}" data-platform-name="${i}" />`
      }
      <input
        class="platform-url-input"
        type="url"
        placeholder="${p.id === "google" ? "https://g.page/r/..." : p.id === "facebook" ? "https://facebook.com/YOURPAGE/reviews" : "https://"}"
        value="${escapeHtml(p.url)}"
        data-platform-url="${i}"
      />
      ${p.id !== "google" && p.id !== "facebook"
        ? `<button type="button" class="platform-remove-btn" data-remove-platform="${i}" title="Remove">✕</button>`
        : ""
      }
    </div>
  `).join("");
}

function renderProfilePage(appData: AppData | null, platforms: ReviewPlatform[], isTesting = false) {
  const profile = appData?.profile;
  const account = appData?.account;

  const req = (filled: boolean, key: string) =>
    `<span class="field-status-badge ${filled ? "badge-done" : "badge-needed"}" data-field-badge="${key}">${filled ? "✓" : "✗"}</span>`;

  const hasReviewPlatform = platforms.some((p) => p.enabled && p.url.trim());

  return `
    <h2 class="page-heading">Business profile</h2>
    <section class="panel">
      <form class="profile-form-grid" data-profile-form>
        <label class="field">
          <span>Location name</span>
          <input
            name="location_name"
            value="${escapeHtml(profile?.location_name ?? "")}"
            placeholder="e.g. Downtown, North Location"
          />
        </label>
        <label class="field">
          <span>Business Name ${req(Boolean(profile?.business_name?.trim()), "business_name")}</span>
          <input
            name="business_name"
            value="${escapeHtml(profile?.business_name ?? "")}"
            maxlength="30"
          />
          <span class="field-help"><em>Displayed in text messages.</em> Max 30 characters.</span>
        </label>
        <label class="field field-span-2">
          <span>Business address ${req(Boolean(profile?.business_address?.trim()), "business_address")}</span>
          <textarea
            name="business_address"
            rows="3"
          >${escapeHtml(profile?.business_address ?? "")}</textarea>
        </label>
        <label class="field">
          <span>Business phone ${req(Boolean(profile?.business_phone?.trim()), "business_phone")}</span>
          <input
            name="business_phone"
            type="tel"
            value="${escapeHtml(profile?.business_phone ?? "")}"
          />
        </label>
        <label class="field">
          <span>Your name ${req(Boolean(profile?.contact_name?.trim()), "contact_name")}</span>
          <input
            name="contact_name"
            value="${escapeHtml(profile?.contact_name ?? "")}"
          />
        </label>
        <label class="field">
          <span>Your email ${req(Boolean(profile?.primary_email?.trim()), "primary_email")}</span>
          <input
            name="primary_email"
            type="email"
            value="${escapeHtml(profile?.primary_email ?? "")}"
          />
        </label>
        <label class="field">
          <span>Time zone</span>
          <select name="timezone">
            ${[
              ["America/New_York",    "Eastern — New York, Miami, Atlanta"],
              ["America/Chicago",     "Central — Chicago, Dallas, Houston"],
              ["America/Denver",      "Mountain — Denver, Salt Lake City"],
              ["America/Phoenix",     "Mountain (no DST) — Phoenix, Arizona"],
              ["America/Los_Angeles", "Pacific — Los Angeles, Seattle"],
              ["America/Anchorage",   "Alaska"],
              ["Pacific/Honolulu",    "Hawaii"],
              ["Europe/London",       "London (GMT/BST)"],
              ["Europe/Paris",        "Central Europe — Paris, Berlin, Rome"],
              ["Australia/Sydney",    "Sydney, Australia"],
            ].map(([val, label]) =>
              `<option value="${val}"${(profile?.timezone ?? "America/Chicago") === val ? " selected" : ""}>${label}</option>`
            ).join("")}
          </select>
          <span class="field-help">Used for timestamps in notification emails.</span>
        </label>
        <div class="field field-span-2">
          <span class="field-label">
            Review platforms ${req(hasReviewPlatform, "review_platforms")}
          </span>
          <small class="field-help">
            Customers who reply YES get a link to this page. Add the URLs where you want them to leave reviews.
            At least one platform must be enabled with a URL.
          </small>
          <div class="platform-editor" data-platform-editor>
            ${renderPlatformEditorRows(platforms)}
          </div>
          <button type="button" class="platform-add-btn" data-add-platform>+ Add platform</button>
        </div>
        <details class="help-disclosure">
          <summary>How to get your Google review link</summary>
          <div class="help-disclosure-body">
            <ol>
              <li>Open your Google Business Profile at <a href="https://business.google.com/" target="_blank" rel="noreferrer">business.google.com</a>.</li>
              <li>Select <strong>Read Reviews</strong>, then choose <strong>Get more reviews</strong>.</li>
              <li>Copy your review link and paste it into the Google URL field above.</li>
            </ol>
          </div>
        </details>
        <div class="field field-span-2 report-pref-field">
          <span class="field-label">Summary email reports</span>
          <div class="report-pref-options">
            ${[
              ["weekly",         "Once a Week (Monday morning summary)"],
              ["daily_activity", "Every Day with Activity"],
              ["daily_always",   "Every Weekday"],
              ["none",           "None"],
            ].map(([val, label]) => `
              <label class="report-pref-option">
                <input type="radio" name="report_preference" value="${val}"
                  ${(profile?.report_preference ?? "daily_activity") === val ? " checked" : ""}>
                <span>${label}</span>
              </label>`).join("")}
          </div>
          <small class="field-help">Form submission notifications always send immediately regardless of this setting.</small>
        </div>
        <button class="primary-button profile-save-btn" type="button" data-save-profile>Save profile</button>
      </form>
    </section>
    ${isTesting ? `
    <details class="danger-zone">
      <summary>Testing: reset data</summary>
      <div class="danger-zone-body">
        <p><strong>Refill tokens (+250)</strong> — adds 250 tokens without going through billing. Use when tokens were drained by failed test sends.</p>
        <button class="secondary-button" data-refill-tokens>Add 250 tokens</button>
        <hr style="margin:16px 0;border-color:#fca5a5" />
        <p><strong>Clear phone numbers</strong> — removes recent-send and opt-out blockers for this location so test numbers can be used again. Enter one or more numbers separated by commas, spaces, or new lines. Activity log entries stay in place.</p>
        <form class="inline-form" onsubmit="return false">
          <textarea
            name="test_phone_number"
            class="text-input"
            rows="2"
            placeholder="(601) 555-1234, (601) 555-5678"
          ></textarea>
          <button type="button" class="secondary-button" data-clear-phone-blockers>Clear phone numbers</button>
        </form>
        <hr style="margin:16px 0;border-color:#fca5a5" />
        <p><strong>Clear batches &amp; phone history</strong> — deletes all upload batches and suppression history. Profile and billing stay intact. Use this to re-test with numbers that have already been messaged.</p>
        <button class="danger-button" data-clear-batches>Clear all batches &amp; history</button>
        <hr style="margin:16px 0;border-color:#fca5a5" />
        <p><strong>Delete account</strong> — permanently deletes all Firestore data and removes your login.</p>
        <button class="danger-button" data-delete-account>Delete my account data</button>
      </div>
    </details>
    ` : ""}
  `;
}

function renderProfileSelectPage(appData: AppData | null) {
  const profiles = appData?.profiles ?? [];

  const locationRows = profiles.map((p) => `
    <li class="location-item" data-select-profile="${escapeHtml(p.id)}">
      <div class="location-name">${escapeHtml(p.location_name || p.business_name || "Unnamed location")}</div>
      ${p.business_address ? `<div class="location-address">${escapeHtml(p.business_address)}</div>` : `<div class="location-address location-address-empty">No address on file</div>`}
    </li>
  `).join("");

  return `
    <div class="page-hero">
      <div class="eyebrow">Rate Follow Up</div>
      <h2 class="page-title">Your locations</h2>
    </div>
    ${profiles.length > 0 ? `<ul class="location-list">${locationRows}</ul>` : ""}
    <section class="create-location-section">
      <form class="stack-panel" data-create-profile-form>
        <label class="field">
          <span>Location name</span>
          <input
            name="location_name"
            placeholder="e.g. Downtown, North Location, Main Street"
            required
          />
        </label>
        <button class="primary-button create-location-btn" type="submit">Create location</button>
      </form>
    </section>
  `;
}

function renderTeamPage(
  authorizedUsers: AccountAccess[] | null,
  loading: boolean,
  busy = false,
) {
  const rows = loading
    ? `<p class="muted">Loading…</p>`
    : authorizedUsers === null
    ? `<p class="muted">Not loaded.</p>`
    : authorizedUsers.length === 0
    ? `<p class="muted">No team members yet. Add someone below.</p>`
    : `
      <div class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Added by</th>
              <th>Added</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            ${authorizedUsers.map((u) => `
              <tr>
                <td>${escapeHtml(u.authorized_email)}</td>
                <td class="muted-sm">${escapeHtml(u.added_by)}</td>
                <td class="muted-sm">${formatDate(u.created_at)}</td>
                <td>
                  <button
                    class="secondary-button small-button"
                    data-remove-authorized-user="${escapeHtml(u.authorized_email)}"
                  >Remove</button>
                </td>
              </tr>
            `).join("")}
          </tbody>
        </table>
      </div>
    `;

  return `
    <h2 class="page-heading">Team</h2>
    <section class="panel stack-panel">
      <h3>Authorized users</h3>
      <p class="muted-sm">Users you add here can sign in with their own account and access your locations, upload numbers, and send messages using your billing. They cannot change billing or payment details.</p>
      ${rows}
    </section>
    <section class="panel stack-panel">
      <h3>Add a team member</h3>
      <p class="muted-sm">Enter their email address. They must sign in to the app with this address before access takes effect.</p>
      <form class="inline-form" onsubmit="return false">
        <input
          type="email"
          name="new_authorized_email"
          class="text-input"
          placeholder="colleague@example.com"
          ${busy ? "disabled" : ""}
        />
        <button type="button" class="primary-button" data-add-authorized-user ${busy ? "disabled" : ""}>${busy ? "Adding…" : "Add to team"}</button>
      </form>
    </section>
  `;
}

function renderTestAccountsPage(
  testingAccounts: TestingAccount[] | null,
  loading: boolean,
  currentUserEmail?: string,
) {
  const rows = loading
    ? `<p class="muted">Loading…</p>`
    : testingAccounts === null
    ? `<p class="muted">Not loaded.</p>`
    : testingAccounts.length === 0
    ? `<p class="muted">No testing accounts yet.</p>`
    : `<table class="data-table">
        <thead><tr><th>Email</th><th>Added by</th><th>Added</th><th></th></tr></thead>
        <tbody>
          ${testingAccounts.map((a) => `
            <tr>
              <td>${escapeHtml(a.email)}</td>
              <td>${escapeHtml(a.added_by)}</td>
              <td>${escapeHtml(a.created_at.slice(0, 10))}</td>
              <td><button class="secondary-button small-button" data-remove-testing-account="${escapeHtml(a.email)}">Remove</button></td>
            </tr>
          `).join("")}
        </tbody>
      </table>`;

  return `
    <h2 class="page-heading">Test accounts</h2>
    <section class="panel">
      <div class="test-accounts-header">
        <p class="muted-sm" style="margin:0">Testing accounts can add tokens, clear batches, and reset profile data without going through billing. Only testing accounts can access this page or add new entries.</p>
        ${currentUserEmail ? `<button class="secondary-button small-button" data-revert-tester style="white-space:nowrap">Revert to non-tester</button>` : ""}
      </div>
      ${rows}
      <form class="test-account-add-form" style="margin-top:20px">
        <label class="field" style="max-width:360px">
          <span>Add testing account</span>
          <input name="new_testing_email" type="email" placeholder="email@example.com" autocomplete="off" />
        </label>
        <button type="button" class="primary-button" data-add-testing-account style="margin-top:8px">Add account</button>
      </form>
    </section>
  `;
}

function renderAdminAccountsPage(accounts: AdminAccountOverview[] | null, loading: boolean) {
  const rows = loading
    ? `<p class="muted">Loading…</p>`
    : accounts === null
    ? `<p class="muted">Not loaded.</p>`
    : accounts.length === 0
    ? `<p class="muted">No accounts yet.</p>`
    : `<div class="table-wrapper"><table class="data-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Account ID</th>
            <th>Profiles</th>
            <th>Billing status</th>
            <th>Plan <code class="field-name">usage_plan_id</code></th>
            <th>Tokens</th>
            <th>Square customer <code class="field-name">square_customer_id</code></th>
            <th>Square subscription <code class="field-name">square_subscription_id</code></th>
          </tr>
        </thead>
        <tbody>
          ${accounts.map((a) => {
            const profiles = a.profiles ?? [];
            return `
            <tr>
              <td>${escapeHtml(a.email || "unknown")}</td>
              <td><code>${escapeHtml(a.account_id)}</code></td>
              <td>${profiles.length > 0
                ? escapeHtml(profiles.map((p) => p.business_name || p.profile_id).join(", "))
                : `<span class="muted-sm">none</span>`}</td>
              <td>${a.operates_under_email
                ? `Operates under <strong>${escapeHtml(a.operates_under_email)}</strong>'s billing`
                : escapeHtml(a.billing_status)}</td>
              <td>${a.plan_mismatch
                ? `<span class="admin-warning" title="Stored plan disagrees with Square">⚠ stored: ${escapeHtml(a.usage_plan_id || "—")}, Square says: ${escapeHtml(a.real_plan_id)}</span>`
                : a.usage_plan_id ? escapeHtml(a.usage_plan_id) : `<span class="muted-sm">—</span>`}</td>
              <td>${formatNumber(a.token_balance)}</td>
              <td>${a.square_customer_id ? `<code>${escapeHtml(a.square_customer_id)}</code>` : `<span class="muted-sm">—</span>`}</td>
              <td>${a.square_subscription_id ? `<code>${escapeHtml(a.square_subscription_id)}</code>` : `<span class="muted-sm">—</span>`}</td>
            </tr>
          `;
          }).join("")}
        </tbody>
      </table></div>`;

  return `
    <h2 class="page-heading">Admin — accounts overview</h2>
    <section class="panel">
      <p class="muted-sm" style="margin:0 0 16px">Every account in Firestore, cross-referenced with its Firebase Auth email and profiles. Admin-only (issue #53).</p>
      ${rows}
    </section>
  `;
}

// Match the backend's adminActivityLogLimit/adminPhoneNumbersLimit (admin.go)
// — purely descriptive text, not enforced client-side.
const adminActivityLogLimitLabel = 200;
const adminPhoneNumbersLimitLabel = 500;

const adminLogEventLabel: Record<string, string> = {
  loaded: "Loaded into the system",
  rejected: "Rejected",
  suppressed: "Suppressed",
  sent: "Sent opt-in message",
  send_failed: "Send failed",
  skipped: "Skipped",
  recv_yes: "Replied YES",
  recv_no: "Replied NO",
  opted_out: "Opted out",
  clicked_review: "Clicked review link",
  feedback: "Submitted feedback",
};

const adminLogEventClass: Record<string, string> = {
  loaded: "log-event-loaded",
  rejected: "log-event-rejected",
  suppressed: "log-event-suppressed",
  sent: "log-event-sent",
  send_failed: "log-event-failed",
  skipped: "log-event-skipped",
  recv_yes: "log-event-yes",
  recv_no: "log-event-no",
  opted_out: "log-event-opted-out",
  clicked_review: "log-event-yes",
  feedback: "log-event-yes",
};

function renderAdminActivityLogPage(entries: AdminActivityLogEntry[] | null, loading: boolean, filter: string) {
  const filterDigits = filter.replace(/\D/g, "");
  const filtered = entries?.filter((e) => !filterDigits || e.phone.replace(/\D/g, "").includes(filterDigits)) ?? null;

  const body = loading
    ? `<p class="muted">Loading…</p>`
    : entries === null
    ? `<p class="muted">Not loaded.</p>`
    : filtered && filtered.length === 0
    ? `<p class="muted">No activity matches "${escapeHtml(filter)}".</p>`
    : `<div class="table-wrapper"><table class="data-table">
        <thead>
          <tr>
            <th>Timestamp</th>
            <th>Account</th>
            <th>Business</th>
            <th>Phone</th>
            <th>Event</th>
            <th>Detail</th>
          </tr>
        </thead>
        <tbody>
          ${(filtered ?? []).map((e) => `
            <tr class="log-row ${adminLogEventClass[e.event] ?? ""}">
              <td>${escapeHtml(formatLogTimestamp(e.timestamp))}</td>
              <td>${escapeHtml(e.account_email || e.account_id)}</td>
              <td>${escapeHtml(e.business_name || e.profile_id)}</td>
              <td>${escapeHtml(formatPhoneDisplay(e.phone))}</td>
              <td>${escapeHtml(adminLogEventLabel[e.event] ?? e.event)}</td>
              <td>${escapeHtml(e.detail)}</td>
            </tr>
          `).join("")}
        </tbody>
      </table></div>`;

  return `
    <h2 class="page-heading">Admin — activity log</h2>
    <section class="panel">
      <p class="muted-sm" style="margin:0 0 16px">The ${adminActivityLogLimitLabel} most recent events across every customer.</p>
      <label class="field" style="max-width:280px;margin-bottom:16px">
        <span>Filter by phone number</span>
        <input type="text" value="${escapeHtml(filter)}" placeholder="Search phone…" data-admin-filter="activity-log" />
      </label>
      ${body}
    </section>
  `;
}

function renderAdminPhoneNumbersPage(entries: AdminPhoneNumberEntry[] | null, loading: boolean, filter: string) {
  const filterDigits = filter.replace(/\D/g, "");
  const filtered = entries?.filter((e) => !filterDigits || e.phone_number.replace(/\D/g, "").includes(filterDigits)) ?? null;

  const body = loading
    ? `<p class="muted">Loading…</p>`
    : entries === null
    ? `<p class="muted">Not loaded.</p>`
    : filtered && filtered.length === 0
    ? `<p class="muted">No numbers match "${escapeHtml(filter)}".</p>`
    : `<div class="table-wrapper"><table class="data-table">
        <thead>
          <tr>
            <th>Phone</th>
            <th>Account</th>
            <th>Business</th>
            <th>State</th>
            <th>Last contacted</th>
            <th>Updated</th>
          </tr>
        </thead>
        <tbody>
          ${(filtered ?? []).map((e) => `
            <tr>
              <td>${escapeHtml(formatPhoneDisplay(e.phone_number))}</td>
              <td>${escapeHtml(e.account_email || e.account_id)}</td>
              <td>${escapeHtml(e.business_name || e.profile_id)}</td>
              <td>${escapeHtml(e.state)}</td>
              <td>${e.last_contacted_at ? escapeHtml(formatLogTimestamp(e.last_contacted_at)) : `<span class="muted-sm">—</span>`}</td>
              <td>${escapeHtml(formatLogTimestamp(e.updated_at))}</td>
            </tr>
          `).join("")}
        </tbody>
      </table></div>`;

  return `
    <h2 class="page-heading">Admin — phone numbers</h2>
    <section class="panel">
      <p class="muted-sm" style="margin:0 0 16px">The ${adminPhoneNumbersLimitLabel} most recently updated conversations across every customer.</p>
      <label class="field" style="max-width:280px;margin-bottom:16px">
        <span>Filter by phone number</span>
        <input type="text" value="${escapeHtml(filter)}" placeholder="Search phone…" data-admin-filter="phone-numbers" />
      </label>
      ${body}
    </section>
  `;
}

function renderAdminOptOutsPage(entries: AdminOptOutEntry[] | null, loading: boolean) {
  const body = loading
    ? `<p class="muted">Loading…</p>`
    : entries === null
    ? `<p class="muted">Not loaded.</p>`
    : entries.length === 0
    ? `<p class="muted">No numbers are currently globally opted out.</p>`
    : `<div class="table-wrapper"><table class="data-table">
        <thead>
          <tr>
            <th>Phone</th>
            <th>First opted out</th>
            <th>Last STOP</th>
            <th>Expires</th>
            <th>STOP count</th>
            <th>Originating account</th>
          </tr>
        </thead>
        <tbody>
          ${entries.map((e) => `
            <tr>
              <td>${escapeHtml(formatPhoneDisplay(e.phone_number))}</td>
              <td>${escapeHtml(formatLogTimestamp(e.opted_out_at))}</td>
              <td>${escapeHtml(formatLogTimestamp(e.last_stop_at))}</td>
              <td>${escapeHtml(formatLogTimestamp(e.expires_at))}</td>
              <td>${formatNumber(e.stop_count)}</td>
              <td><code>${escapeHtml(e.source_account_id)}</code></td>
            </tr>
          `).join("")}
        </tbody>
      </table></div>`;

  return `
    <h2 class="page-heading">Admin — global opt-outs</h2>
    <section class="panel">
      <p class="muted-sm" style="margin:0 0 16px">Every number currently on the cross-customer opt-out list (issue #38) — blocked from every account's sends until it expires, 6 months after the last STOP.</p>
      ${body}
    </section>
  `;
}

const adminCustomerStatsRangeLabel: Record<AdminCustomerStatsRange, string> = {
  week: "Past week",
  month: "Past month",
  quarter: "Past quarter",
};

// renderAdminCustomerStatsPage — issue #68: pick a customer (account) and see
// usage/effectiveness totals for a trailing week/month/quarter, summed across
// every profile under that account plus a per-profile breakdown. The account
// picker reuses whatever the Accounts tab has already fetched rather than
// issuing a second full-account-list request.
function renderAdminCustomerStatsPage(
  accounts: AdminAccountOverview[] | null,
  accountsLoading: boolean,
  selectedAccountId: string | null,
  range: AdminCustomerStatsRange,
  stats: AdminCustomerStatsData | null,
  statsLoading: boolean,
) {
  const accountOptions = (accounts ?? [])
    .slice()
    .sort((a, b) => (a.email || a.account_id).localeCompare(b.email || b.account_id))
    .map((a) => `
      <option value="${escapeHtml(a.account_id)}" ${a.account_id === selectedAccountId ? "selected" : ""}>
        ${escapeHtml(a.email || a.account_id)}
      </option>
    `).join("");

  const rangeTabs = (["week", "month", "quarter"] as AdminCustomerStatsRange[]).map((r) => `
    <button
      type="button"
      class="upload-tab ${range === r ? "upload-tab-active" : ""}"
      data-admin-range="${r}"
    >${adminCustomerStatsRangeLabel[r]}</button>
  `).join("");

  let body: string;
  if (!selectedAccountId) {
    body = `<p class="muted">Select a customer above to see their usage stats.</p>`;
  } else if (statsLoading || stats === null) {
    body = `<p class="muted">Loading…</p>`;
  } else {
    const profileRows = stats.profiles.length === 0
      ? `<tr><td colspan="4" class="muted">No profiles for this account.</td></tr>`
      : stats.profiles.map((p) => `
          <tr>
            <td>${escapeHtml(p.business_name || p.profile_id)}</td>
            <td>${formatNumber(p.messages_sent)}</td>
            <td>${formatNumber(p.unique_numbers_contacted)}</td>
            <td>${formatNumber(p.review_clicks)}</td>
          </tr>
        `).join("");

    body = `
      <section class="grid compact-grid">
        ${renderMetric("Messages sent", formatNumber(stats.messages_sent))}
        ${renderMetric("Unique numbers contacted", formatNumber(stats.unique_numbers_contacted))}
        ${renderMetric("Review link clicks", formatNumber(stats.review_clicks))}
      </section>
      <div class="table-wrapper"><table class="data-table">
        <thead>
          <tr>
            <th>Profile</th>
            <th>Messages sent</th>
            <th>Unique numbers</th>
            <th>Review clicks</th>
          </tr>
        </thead>
        <tbody>${profileRows}</tbody>
      </table></div>
    `;
  }

  return `
    <h2 class="page-heading">Admin — customer stats</h2>
    <section class="panel">
      <p class="muted-sm" style="margin:0 0 16px">Usage and effectiveness for a single customer, across every one of their profiles (issue #68).</p>
      <div class="admin-stats-controls" style="display:flex;gap:16px;align-items:flex-end;flex-wrap:wrap;margin-bottom:16px">
        <label class="field" style="max-width:320px">
          <span>Customer</span>
          <select data-admin-customer-select ${accountsLoading ? "disabled" : ""}>
            <option value="">${accountsLoading ? "Loading customers…" : "Select a customer…"}</option>
            ${accountOptions}
          </select>
        </label>
        <div class="upload-tabs">${rangeTabs}</div>
      </div>
      ${body}
    </section>
  `;
}

function formatCents(amountCents: number, currency: string): string {
  const amount = (amountCents / 100).toFixed(2);
  return currency && currency !== "USD" ? `${amount} ${currency}` : `$${amount}`;
}

function renderAdminTransactionsPage(data: AdminTransactionsData | null, loading: boolean) {
  if (loading) return `<p class="muted">Loading…</p>`;
  if (data === null) return `<p class="muted">Not loaded.</p>`;

  const subscriptionsBody = data.subscriptions.length === 0
    ? `<p class="muted">No subscriptions on file with Square.</p>`
    : `<div class="table-wrapper"><table class="data-table">
        <thead>
          <tr>
            <th>Account</th>
            <th>Real plan <code class="field-name">Square</code></th>
            <th>Stored plan <code class="field-name">usage_plan_id</code></th>
            <th>Status</th>
            <th>Created</th>
            <th>Charged through</th>
            <th>Canceled</th>
            <th>Real subscription ID <code class="field-name">Square</code></th>
            <th>Stored subscription ID <code class="field-name">square_subscription_id</code></th>
          </tr>
        </thead>
        <tbody>
          ${data.subscriptions.map((s) => `
            <tr>
              <td>${escapeHtml(s.email || s.account_id || "unknown")}</td>
              <td>${escapeHtml(s.plan_id || "—")}</td>
              <td>${s.plan_mismatch
                ? `<span class="admin-warning" title="Account's stored usage_plan_id doesn't match what Square is actually billing this subscription for">⚠ ${escapeHtml(s.stored_plan_id || "not stored on account")}</span>`
                : escapeHtml(s.stored_plan_id || "—")}</td>
              <td>${escapeHtml(s.status)}</td>
              <td>${escapeHtml(formatLogTimestamp(s.created_at))}</td>
              <td>${escapeHtml(s.charged_through_date || "—")}</td>
              <td>${escapeHtml(s.canceled_date || "—")}</td>
              <td><code>${escapeHtml(s.subscription_id)}</code></td>
              <td>${s.missing_from_account
                ? `<span class="admin-warning" title="Account's stored square_subscription_id doesn't match this real Square subscription — its cancel button can't find the right subscription (issue #60)">⚠ ${s.stored_subscription_id ? `stored: ${escapeHtml(s.stored_subscription_id)}` : "not stored on account"}</span>`
                : `<code>${escapeHtml(s.stored_subscription_id)}</code>`}</td>
            </tr>
          `).join("")}
        </tbody>
      </table></div>`;

  const paymentsBody = data.payments.length === 0
    ? `<p class="muted">No payments on file with Square.</p>`
    : `<div class="table-wrapper"><table class="data-table">
        <thead>
          <tr>
            <th>Account</th>
            <th>Amount</th>
            <th>Status</th>
            <th>Date</th>
            <th>Payment ID</th>
          </tr>
        </thead>
        <tbody>
          ${data.payments.map((p) => `
            <tr>
              <td>${escapeHtml(p.email || p.account_id || "unknown")}</td>
              <td>${escapeHtml(formatCents(p.amount_cents, p.currency))}</td>
              <td>${escapeHtml(p.status)}</td>
              <td>${escapeHtml(formatLogTimestamp(p.created_at))}</td>
              <td><code>${escapeHtml(p.payment_id)}</code></td>
            </tr>
          `).join("")}
        </tbody>
      </table></div>`;

  return `
    <h2 class="page-heading">Admin — transactions</h2>
    <section class="panel">
      <h3 style="margin-top:0">Subscriptions</h3>
      <p class="muted-sm" style="margin:0 0 16px">Every subscription Square has on file, cross-referenced against each account's stored square_subscription_id. A flagged row is why that account's cancel button doesn't show (issue #60) — report only, nothing here is written back automatically.</p>
      ${subscriptionsBody}
    </section>
    <section class="panel" style="margin-top:24px">
      <h3 style="margin-top:0">Payments</h3>
      <p class="muted-sm" style="margin:0 0 16px">Every payment Square has on file, including ones from before subscriptions existed (issue #54).</p>
      ${paymentsBody}
    </section>
  `;
}

function renderUploadPage(
  appData: AppData | null,
  csvResult: CsvValidationResult | null,
  csvFileName: string,
  lastSavedUploadId: string,
  selectedUploadDetails: UploadDetails | null,
  uploadAttested: boolean,
  uploadMode: "file" | "manual",
  sendStatus: "idle" | "sending" | "complete" | "failed" | "stopped" = "idle",
  sendResult: SendResult | null = null,
  isTesting = false,
  showClosedBatches = false,
) {
  const profileComplete = Boolean(appData?.profile?.is_profile_complete);
  const uploads = appData?.uploads ?? [];
  const canUpload = profileComplete && uploadAttested;
  const billingDone = isTesting ? true : Boolean(appData?.account?.square_card_id);
  const readyToSendCount = uploads
    .filter((u) => u.status === "validated")
    .reduce((sum, u) => sum + u.valid_rows, 0);

  return `
    <section class="panel stack-panel">
      <h3>Add phone numbers</h3>
      ${!profileComplete ? `<p class="muted">Complete your profile before uploading.</p>` : ""}
      <label class="attest-label ${profileComplete ? "" : "disabled"}">
        <input
          type="checkbox"
          data-attest-checkbox
          ${disabledAttr(!profileComplete)}
          ${uploadAttested ? "checked" : ""}
        />
        <span>I confirm that all phone numbers belong to customers who have consented
        to receive messages, and that consent records are held for at least 30 days.</span>
      </label>
      <div class="upload-tabs">
        <button
          class="upload-tab ${uploadMode === "file" ? "upload-tab-active" : ""}"
          data-upload-tab="file"
          ${disabledAttr(!profileComplete)}
        >Upload file</button>
        <button
          class="upload-tab ${uploadMode === "manual" ? "upload-tab-active" : ""}"
          data-upload-tab="manual"
          ${disabledAttr(!profileComplete)}
        >Type / paste numbers</button>
      </div>
      ${uploadMode === "file" ? `
        <p class="muted-sm">CSV, Excel (.xlsx), or plain text — one number per line. Mixed formats OK (dashes, parentheses, +1 all accepted). First column used for multi-column files.</p>
        <div class="field batch-name-field">
          <label class="label">Batch name <span class="field-help">(optional — defaults to filename)</span></label>
          <input class="input" type="text" data-batch-name placeholder="e.g. July follow-up" maxlength="80" ${disabledAttr(!canUpload)} />
        </div>
        <label class="upload-drop ${canUpload ? "" : "disabled"}">
          <span>Select file</span>
          <input type="file" accept=".csv,.xlsx,.xls,.txt,text/csv,text/plain" data-file-input ${disabledAttr(!canUpload)} />
        </label>
      ` : `
        <p class="muted-sm">Enter one phone number per line (or comma/semicolon-separated). Any format accepted.</p>
        <div class="field batch-name-field">
          <label class="label">Batch name <span class="field-help">(optional)</span></label>
          <input class="input" type="text" data-batch-name placeholder="e.g. July follow-up" maxlength="80" ${disabledAttr(!canUpload)} />
        </div>
        <textarea
          class="manual-entry-textarea"
          data-manual-text
          rows="8"
          placeholder="6015551234&#10;(601) 555-5678&#10;+1 601-555-9012"
          ${disabledAttr(!canUpload)}
        ></textarea>
        <button class="primary-button" data-manual-add ${disabledAttr(!canUpload)}>Save as new batch</button>
      `}
    </section>
    <section class="panel stack-panel">
      <h3>Saved batches</h3>
      ${uploads.length === 0
        ? `<p>No batches yet.</p>`
        : `
          ${billingDone && readyToSendCount > 0 ? `
            <p class="muted-sm">Numbers recently contacted are automatically skipped as repeats.</p>
            ${renderSendStatusBadge(sendStatus, sendResult)}
            <div class="welcome-actions">
              <button class="primary-button" data-send-all ${sendStatus === "sending" ? "disabled" : ""}>${sendStatus === "sending" ? "Sending…" : "Send now"}</button>
            </div>
          ` : !billingDone ? `
            <p class="muted-sm">Set up billing to start sending. <a href="/billing" data-nav>Set up billing →</a></p>
          ` : `
            <p class="muted-sm">No batches are ready to send. Upload and validate a batch to get started.</p>
          `}
          ${renderUploadBatchTable(uploads, selectedUploadDetails?.upload.id ?? lastSavedUploadId, showClosedBatches)}
        `
      }
    </section>
    ${
      csvResult
        ? `
          <section class="panel stack-panel">
            <h3>Validation results ${csvFileName ? `for ${escapeHtml(csvFileName)}` : ""}</h3>
            ${lastSavedUploadId ? `<p class="muted">Saved: ${escapeHtml(lastSavedUploadId)}</p>` : ""}
            <section class="grid compact-grid">
              ${renderMetric("Rows read", String(csvResult.summary.totalRows))}
              ${renderMetric("Valid", String(csvResult.summary.validRows))}
              ${renderMetric("Rejected", String(csvResult.summary.rejectedRows))}
            </section>
            ${renderValidationTable(csvResult)}
          </section>
        `
        : ""
    }
    ${selectedUploadDetails ? renderSelectedUploadDetails(selectedUploadDetails) : ""}
  `;
}

function renderBillingPage(
  appData: AppData | null,
  showUpdateForm: boolean,
  isGuest = false,
  isTesting = false,
  selectedPlanId: string | null = null,
  confirmChecked = false,
  demoConfirmChecked = false,
  modalOpen: "plan" | "demo" | "cancel" | null = null,
) {
  const account = appData?.account;
  const activePlanId = account?.usage_plan_id ?? null;
  const hasCard = Boolean(account?.square_card_id);
  const billingReadOnly = isGuest || isTesting;
  const showForm = !billingReadOnly && (!hasCard || showUpdateForm);
  const isActive = account?.billing_status === "active";
  const demoTokens = 12;

  // Only highlight a plan the user explicitly clicked — never auto-select based on active plan
  const highlightedPlanId = selectedPlanId;

  const planCards = usagePlans.map((plan) => {
    const isHighlighted = plan.id === highlightedPlanId;
    // "Current plan" requires a real Square subscription behind it
    // (square_subscription_id), not just billing_status/usage_plan_id —
    // accounts billed once via the legacy one-off Payments flow (before
    // issue #54 added real recurring subscriptions) can have an active
    // status and a plan id with no actual subscription in Square. Showing
    // "Current plan" for those is misleading, since there's nothing
    // recurring to be "on" (issue #60 follow-up).
    const isCurrent = plan.id === activePlanId && isActive && Boolean(account?.square_subscription_id);
    return `
      <div class="plan-card ${isHighlighted ? "plan-card-active" : ""} ${isCurrent ? "plan-card-current" : ""}">
        <div class="plan-card-name">${escapeHtml(plan.label)}</div>
        <div class="plan-card-price">$${plan.monthlyPrice}<span class="plan-card-period">/mo</span></div>
        <div class="plan-card-messages">${formatNumber(plan.includedMessages)} tokens/month</div>
        ${isCurrent
          ? `<div class="plan-card-badge">Current plan</div>`
          : billingReadOnly
          ? ""
          : `<button class="secondary-button small-button plan-card-select${isHighlighted ? " plan-card-select-active" : ""}" data-plan-id="${plan.id}">${isHighlighted ? "Selected" : "Select"}</button>`
        }
      </div>
    `;
  }).join("");

  const confirmPlan = selectedPlanId ? usagePlans.find((p) => p.id === selectedPlanId) : null;

  // Beneath the two upper-tier plan cards (grid-column 2/4 of the 3-column plan grid), right-aligned
  const demoPanel = !billingReadOnly ? `
    <div class="demo-panel">
      <div class="demo-panel-sub">$5 One Time Purchase - ${demoTokens} tokens</div>
      <button class="demo-button${hasCard ? " demo-button-active" : ""}" data-demo-open ${hasCard ? "" : "disabled"}>$5 Demo</button>
      ${!hasCard ? `<span class="demo-panel-hint">Save a payment method to unlock</span>` : ""}
    </div>
  ` : "";

  // Reveals a "Subscribe to Plan" trigger once both a card is saved and a plan is picked
  const planTrigger = !billingReadOnly && hasCard ? (
    confirmPlan
      ? `<button class="primary-button" style="margin-top:14px" data-plan-open>Subscribe to Plan</button>`
      : `<p class="muted-sm" style="margin:14px 0 0">Select a plan above to continue.</p>`
  ) : "";

  const nextChargeDate = new Date();
  nextChargeDate.setDate(nextChargeDate.getDate() + 30);
  const nextChargeDateLabel = nextChargeDate.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

  const planModal = modalOpen === "plan" && confirmPlan ? `
    <div class="modal-backdrop" data-close-billing-modal>
      <div class="modal-panel" role="dialog" aria-modal="true">
        <h3 class="panel-section-heading">Confirm subscription</h3>
        <div class="billing-confirm-summary">
          <div class="billing-confirm-plan">${escapeHtml(confirmPlan.label)}</div>
          <div class="billing-confirm-price">$${confirmPlan.monthlyPrice}<span class="billing-confirm-period">/month</span></div>
          <div class="billing-confirm-tokens">${formatNumber(confirmPlan.includedMessages)} tokens included</div>
        </div>
        <p class="muted-sm">You will be charged <strong>$${confirmPlan.monthlyPrice}/month</strong>, renewing automatically on ${nextChargeDateLabel} and each month after.</p>
        <label class="billing-confirm-check">
          <input type="checkbox" id="billing-confirm-checkbox" ${confirmChecked ? "checked" : ""}/>
          I understand I will be charged <strong>$${confirmPlan.monthlyPrice}/month</strong>, recurring automatically until cancelled.
        </label>
        <div id="confirm-purchase-error" class="banner error" style="display:none"></div>
        <div class="modal-actions">
          <button class="secondary-button small-button" data-close-billing-modal-btn>Cancel</button>
          <button class="primary-button" data-confirm-purchase ${confirmChecked ? "" : "disabled"}>Confirm Purchase</button>
        </div>
      </div>
    </div>
  ` : "";

  const demoModal = modalOpen === "demo" ? `
    <div class="modal-backdrop" data-close-billing-modal>
      <div class="modal-panel" role="dialog" aria-modal="true">
        <h3 class="panel-section-heading">Confirm demo purchase</h3>
        <p class="muted-sm">This is a one-time charge of <strong>$5</strong> for <strong>${demoTokens} tokens</strong> — not a subscription.</p>
        <label class="billing-confirm-check">
          <input type="checkbox" id="demo-confirm-checkbox" ${demoConfirmChecked ? "checked" : ""}/>
          I want to be charged <strong>$5 one time</strong> for ${demoTokens} tokens. This is not a subscription.
        </label>
        <div id="demo-charge-error" class="banner error" style="display:none"></div>
        <div class="modal-actions">
          <button class="secondary-button small-button" data-close-billing-modal-btn>Cancel</button>
          <button class="primary-button" data-demo-confirm ${demoConfirmChecked ? "" : "disabled"}>Confirm Purchase</button>
        </div>
      </div>
    </div>
  ` : "";

  const formatPlainDate = (value: string) => {
    try {
      return new Date(`${value}T00:00:00`).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
    } catch {
      return value;
    }
  };

  const cancelScheduled = Boolean(account?.square_subscription_canceled_date);

  // Only offer cancellation once billing is active; once scheduled, swap the
  // trigger for a static status line instead. Deliberately not gated on
  // account?.square_subscription_id being present — the backend falls back
  // to a live Square lookup by square_customer_id when it's missing or
  // stale (issue #60), so an account without that field stored can still
  // reach the cancel flow instead of the button silently never appearing.
  const cancelTrigger = !billingReadOnly && isActive ? (
    cancelScheduled
      ? `<p class="billing-cancel-status">Your subscription is canceled — access continues until ${formatPlainDate(account!.square_subscription_canceled_date!)}.</p>`
      : `<button class="billing-cancel-trigger" data-cancel-open>Want to cancel your subscription? Your next bill is ${account?.next_billing_date ? formatPlainDate(account.next_billing_date) : "—"}</button>`
  ) : "";

  const cancelModal = modalOpen === "cancel" ? `
    <div class="modal-backdrop" data-close-billing-modal>
      <div class="modal-panel" role="dialog" aria-modal="true">
        <h3 class="panel-section-heading">Cancel subscription</h3>
        <p class="muted-sm">Your plan and tokens stay active until <strong>${account?.next_billing_date ? formatPlainDate(account.next_billing_date) : "the end of your current billing period"}</strong>. After that, your card won't be charged again and the subscription ends.</p>
        <div id="cancel-subscription-error" class="banner error" style="display:none"></div>
        <div class="modal-actions">
          <button class="secondary-button small-button" data-close-billing-modal-btn>Keep Plan</button>
          <button class="danger-button" data-cancel-confirm>Cancel Subscription</button>
        </div>
      </div>
    </div>
  ` : "";

  return `
    <h2 class="page-heading">Billing</h2>
    <p class="billing-token-note">It takes 2–3 tokens to send a full message to a number.</p>
    ${isGuest ? `<div class="banner info" style="margin-bottom:16px">Billing is managed by the account owner. You can view plan details but cannot make changes.</div>` : ""}
    ${!isGuest && isTesting ? `<div class="banner info" style="margin-bottom:16px">Testing accounts bypass billing. You can view plan details, but payment setup is disabled while this account is marked for testing.</div>` : ""}

    <section class="panel stack-panel">
      <h3 class="panel-section-heading">Choose your plan</h3>
      <div class="plan-cards">
        ${planCards}
        ${demoPanel}
      </div>
      ${planTrigger}
      <div class="token-balance-row">
        <span class="token-balance-label">Status</span>
        <span>${formatBillingStatus(account?.billing_status ?? "not_configured")}</span>
        ${isActive ? `
          <span class="token-balance-label" style="margin-left:16px">Tokens remaining</span>
          <span class="token-balance-value">${formatNumber(account!.token_balance)}</span>
        ` : ""}
      </div>
    </section>

    <section class="panel stack-panel">
      <h3 class="panel-section-heading">Payment method</h3>

      ${hasCard && !showUpdateForm ? `
        <div class="billing-card-saved">
          ${cardBrandSvg(account?.card_brand ?? "")}
          <div>
            <div class="billing-card-detail">${escapeHtml(formatCardBrand(account?.card_brand ?? "Card"))} ending in ${escapeHtml(account?.card_last4 ?? "")}</div>
            <div class="billing-card-sub">Payment method on file — no charge has been made yet</div>
          </div>
        </div>
        ${!billingReadOnly ? `<button class="secondary-button small-button" style="width:fit-content" data-update-card>Update card</button>` : ""}
        ${cancelTrigger}
      ` : ""}

      ${showForm ? `
        <p class="muted-sm" style="margin:0 0 10px">
          ${hasCard ? "Enter new card details to replace your saved payment method. Your card will not be charged until you confirm a plan." : "Save your card below. You won't be charged until you select a plan and confirm."}
        </p>
        <div id="square-card-container" class="square-card-container"></div>
        <div id="billing-error" class="banner error" style="display:none;margin-top:12px"></div>
        <button class="primary-button" style="margin-top:14px" data-billing-submit>Save payment method</button>
      ` : ""}
    </section>

    ${planModal}
    ${demoModal}
    ${cancelModal}
  `;
}

function renderLoadingPanel() {
  return `
    <section class="panel">
      <h3>Loading data</h3>
      <p>Fetching business account state from Firebase.</p>
    </section>
  `;
}

function renderValidationTable(result: CsvValidationResult) {
  return `
    <div class="table-wrapper">
      <div class="table-toolbar">
        <button class="secondary-button small-button" data-toggle-raw>Show original numbers</button>
      </div>
      <table>
        <thead>
          <tr>
            <th>Row</th>
            <th>Phone (E.164)</th>
            <th class="raw-col">Original</th>
            <th>Status</th>
            <th>Reason</th>
          </tr>
        </thead>
        <tbody>
          ${result.rows
            .map(
              (row) => `
                <tr>
                  <td>${row.row_number}</td>
                  <td>${escapeHtml(row.normalized_phone_number ?? row.phone_number)}</td>
                  <td class="raw-col">${escapeHtml(row.phone_number)}</td>
                  <td>${escapeHtml(formatRowStatus(row.status))}</td>
                  <td>${escapeHtml(row.rejection_reason ?? "")}</td>
                </tr>
              `,
            )
            .join("")}
        </tbody>
      </table>
    </div>
  `;
}

function renderUploadBatchTable(uploads: UploadRecord[], selectedUploadId: string, showClosedBatches = false) {
  const closedCount = uploads.filter((u) => u.status === "closed").length;
  const visible = showClosedBatches ? uploads : uploads.filter((u) => u.status !== "closed");
  return `
    <div class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>Batch name</th>
            <th>Status</th>
            <th>Total</th>
            <th>Sent / ready</th>
            <th>Failed</th>
            <th>Repeats</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          ${visible
            .map(
              (upload) => `
                <tr class="${upload.id === selectedUploadId ? "selected-row" : ""}${upload.status === "closed" ? " closed-row" : ""}">
                  <td>${escapeHtml(upload.batch_name || upload.original_filename)}</td>
                  <td>${escapeHtml(formatUploadStatus(upload.status))}</td>
                  <td>${upload.total_rows}</td>
                  <td>${upload.status === "ready" ? upload.queued_rows : upload.valid_rows}</td>
                  <td>${upload.rejected_rows}</td>
                  <td>${upload.suppressed_rows}</td>
                  <td>${formatDate(upload.created_at)}</td>
                  <td>
                    <div class="table-actions">
                      <button class="secondary-button small-button" type="button" data-select-upload data-upload-id="${escapeHtml(upload.id)}">
                        Review
                      </button>
                      ${upload.status !== "closed" ? `
                        <button class="secondary-button small-button close-batch-btn" type="button" data-close-upload="${escapeHtml(upload.id)}" title="Close batch">
                          Close
                        </button>
                      ` : ""}
                    </div>
                  </td>
                </tr>
              `,
            )
            .join("")}
        </tbody>
      </table>
    </div>
    ${closedCount > 0 ? `
      <button class="closed-batches-toggle" type="button" data-toggle-closed-batches>
        ${showClosedBatches ? `Hide closed batches (${closedCount})` : `Show closed batches (${closedCount})`}
      </button>
    ` : ""}
  `;
}

function renderSelectedUploadDetails(details: UploadDetails) {
  const editable = details.upload.status === "validated";
  const queuedCount = details.rows.filter((r) => r.status === "queued").length;
  const processedStatuses: UploadRow["status"][] = ["optin_sent", "optin_1_sent", "sent", "failed", "skipped_opted_out", "skipped_global_optout", "skipped_recent"];
  const allProcessed = details.rows.length > 0 && queuedCount === 0 && details.rows.every((r) => r.status !== "queued" && r.status !== "valid");
  const noneQueued = !editable && queuedCount === 0 && details.rows.some((r) => processedStatuses.includes(r.status));

  return `
    <section class="panel stack-panel">
      <h3>Batch: ${escapeHtml(details.upload.original_filename)}</h3>
      ${noneQueued ? `
        <div class="banner error" style="margin:0">
          No rows are queued — all numbers in this batch have already been processed.
          Sending now would send 0 messages. To re-test a number, clear batch history first.
        </div>
      ` : ""}
      ${renderBatchStatusBreakdown(details.rows)}
      ${editable
        ? `<p class="muted-sm">Not yet queued — you can add or remove numbers before starting.</p>`
        : ""
      }
      ${renderUploadRowsTable(details.rows, editable, details.upload.id)}
      ${editable ? `
        <div class="add-more-section">
          <h4>Add more numbers to this batch</h4>
          <p class="muted-sm">One per line, or comma/semicolon-separated.</p>
          <textarea class="manual-entry-textarea" data-add-more-text rows="4" placeholder="6015551234&#10;(601) 555-5678"></textarea>
          <button class="secondary-button" data-add-more-rows="${escapeHtml(details.upload.id)}">Add to batch</button>
        </div>
      ` : ""}
    </section>
  `;
}

function renderUploadRowsTable(rows: UploadRow[], editable = false, uploadId = "") {
  return `
    <div class="table-wrapper">
      <div class="table-toolbar">
        <button class="secondary-button small-button" data-toggle-raw>Show original numbers</button>
      </div>
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Phone (E.164)</th>
            <th class="raw-col">Original</th>
            <th>Send pipeline</th>
            <th>Queued at</th>
            ${editable ? "<th></th>" : ""}
          </tr>
        </thead>
        <tbody>
          ${rows
            .map(
              (row) => `
                <tr>
                  <td>${row.row_number}</td>
                  <td>${escapeHtml(row.normalized_phone_number ?? row.phone_number)}</td>
                  <td class="raw-col">${escapeHtml(row.phone_number)}</td>
                  <td>${renderRowPipeline(effectiveRowStatus(row))}</td>
                  <td class="muted-sm">${row.queued_at ? formatDate(row.queued_at) : ""}</td>
                  ${editable && row.id ? `
                    <td>
                      <button
                        class="row-delete-btn"
                        type="button"
                        data-remove-row="${escapeHtml(uploadId)}"
                        data-row-id="${escapeHtml(row.id)}"
                        title="Remove row"
                      >✕</button>
                    </td>
                  ` : editable ? "<td></td>" : ""}
                </tr>
              `,
            )
            .join("")}
        </tbody>
      </table>
    </div>
  `;
}

function renderUploadsTable(
  uploads: Array<{
    original_filename: string;
    total_rows: number;
    valid_rows: number;
    rejected_rows: number;
    queued_rows?: number;
    suppressed_rows?: number;
    created_at: string;
  }>,
) {
  return `
    <div class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>File</th>
            <th>Total rows</th>
            <th>Valid</th>
            <th>Rejected</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          ${uploads
            .map(
              (upload) => `
                <tr>
                  <td>${escapeHtml(upload.original_filename)}</td>
                  <td>${upload.total_rows}</td>
                  <td>${upload.valid_rows}</td>
                  <td>${upload.rejected_rows}</td>
                  <td>${formatDate(upload.created_at)}</td>
                </tr>
              `,
            )
            .join("")}
        </tbody>
      </table>
    </div>
  `;
}

function renderMetric(label: string, value: string) {
  return `
    <article class="metric-card">
      <div class="metric-label">${label}</div>
      <div class="metric-value">${escapeHtml(value)}</div>
    </article>
  `;
}

function renderUsagePlanOptions(account: BusinessAccount | null | undefined) {
  const selectedPlanId = account?.usage_plan_id ?? usagePlans[0].id;

  return usagePlans
    .map(
      (plan) => `
        <option value="${plan.id}" ${plan.id === selectedPlanId ? "selected" : ""}>
          ${escapeHtml(plan.label)} - $${plan.monthlyPrice} / month for ${formatNumber(plan.includedMessages)} messages
        </option>
      `,
    )
    .join("");
}

function getUsagePlan(planId: UsagePlanId | null | undefined) {
  return usagePlans.find((plan) => plan.id === planId) ?? usagePlans[0];
}

function usagePlanIdFromForm(value: FormDataEntryValue | null): UsagePlanId {
  return value === "growth_1000" || value === "scale_5000" || value === "starter_250"
    ? value
    : usagePlans[0].id;
}

function formatNumber(value: number) {
  return new Intl.NumberFormat("en-US").format(value);
}

function cardBrandSvg(brand: string): string {
  const b = brand.toUpperCase().replace(/[\s_]/g, "");
  switch (b) {
    case "VISA":
      return `<svg class="card-brand-svg" width="38" height="24" viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg" aria-label="Visa"><rect width="38" height="24" rx="4" fill="#1A1F71"/><text x="19" y="17" text-anchor="middle" fill="white" font-family="Arial,Helvetica,sans-serif" font-weight="800" font-style="italic" font-size="12" letter-spacing="-0.5">VISA</text></svg>`;
    case "MASTERCARD":
      return `<svg class="card-brand-svg" width="38" height="24" viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg" aria-label="Mastercard"><rect width="38" height="24" rx="4" fill="white" stroke="#e2e8f0" stroke-width="1"/><circle cx="15" cy="12" r="7" fill="#EB001B"/><circle cx="23" cy="12" r="7" fill="#F79E1B" opacity="0.85"/></svg>`;
    case "AMERICANEXPRESS":
      return `<svg class="card-brand-svg" width="38" height="24" viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg" aria-label="American Express"><rect width="38" height="24" rx="4" fill="#2557D6"/><text x="19" y="17" text-anchor="middle" fill="white" font-family="Arial,Helvetica,sans-serif" font-weight="700" font-size="9" letter-spacing="0.5">AMEX</text></svg>`;
    case "DISCOVER":
    case "DISCOVERDINERS":
      return `<svg class="card-brand-svg" width="38" height="24" viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg" aria-label="Discover"><rect width="38" height="24" rx="4" fill="white" stroke="#e2e8f0" stroke-width="1"/><circle cx="26" cy="12" r="9" fill="#FF6600"/><text x="8" y="16" fill="#231F20" font-family="Arial,Helvetica,sans-serif" font-weight="700" font-size="7" letter-spacing="0.3">DISC</text></svg>`;
    default:
      return `<svg class="card-brand-svg" width="38" height="24" viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg" aria-label="Card"><rect width="38" height="24" rx="4" fill="#475569"/><rect x="4" y="8" width="30" height="4" rx="1" fill="white" opacity="0.8"/><rect x="4" y="15" width="8" height="3" rx="1" fill="white" opacity="0.5"/><rect x="14" y="15" width="8" height="3" rx="1" fill="white" opacity="0.5"/></svg>`;
  }
}

function formatCardBrand(brand: string): string {
  const b = brand.toUpperCase().replace(/[\s_]/g, "");
  switch (b) {
    case "VISA": return "Visa";
    case "MASTERCARD": return "Mastercard";
    case "AMERICANEXPRESS": return "American Express";
    case "DISCOVER": return "Discover";
    case "DISCOVERDINERS": return "Discover Diners";
    case "JCB": return "JCB";
    case "CHINAUNIONPAY": return "UnionPay";
    case "INTERAC": return "Interac";
    default: return brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase();
  }
}

function formatBillingStatus(value: string) {
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatUploadStatus(value: UploadRecord["status"]) {
  if (value === "ready") return "Ready / queued";
  if (value === "closed") return "Closed";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatRowStatus(value: UploadRow["status"]) {
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

type PipeStep = { label: string; state: "done" | "fail" | "warn" };

function rowPipelineSteps(status: UploadRow["status"]): PipeStep[] {
  const d = (label: string): PipeStep => ({ label, state: "done" });
  const f = (label: string): PipeStep => ({ label, state: "fail" });
  const w = (label: string): PipeStep => ({ label, state: "warn" });
  switch (status) {
    case "valid":
      return [d("Imported"), d("Valid"), w("Not yet queued")];
    case "queued":
      return [d("Imported"), d("Valid"), d("Queued"), w("Ready to send")];
    case "optin_1_sent":
      return [d("Imported"), d("Valid"), d("Queued"), d("Pt. 1 sent"), w("Pt. 2 sending...")];
    case "optin_sent":
      return [d("Imported"), d("Valid"), d("Queued"), d("Pt. 1 sent"), d("Pt. 2 sent"), w("Awaiting reply")];
    case "sent":
      return [d("Imported"), d("Valid"), d("Queued"), d("Pt. 1 sent"), d("Pt. 2 sent"), d("Replied")];
    case "failed":
      return [d("Imported"), d("Valid"), d("Queued"), f("Send failed")];
    case "rejected_invalid":
      return [d("Imported"), f("Invalid number")];
    case "rejected_duplicate":
      return [d("Imported"), f("Duplicate number")];
    case "rejected_quota":
      return [d("Imported"), f("Quota exceeded")];
    case "rejected_suppressed":
      return [d("Imported"), d("Valid"), f("Suppressed (prior opt-out)")];
    case "rejected_global_optout":
      return [d("Imported"), d("Valid"), f("Globally opted out")];
    case "skipped_opted_out":
      return [d("Imported"), d("Valid"), d("Queued"), f("Skipped — opted out")];
    case "skipped_global_optout":
      return [d("Imported"), d("Valid"), d("Queued"), f("Skipped — opted out globally")];
    case "skipped_recent":
      return [d("Imported"), d("Valid"), d("Queued"), f("Skipped — contacted < 1yr")];
    case "no_phone":
      return [d("Imported"), f("No valid phone number")];
    case "completed_yes":
      return [d("Imported"), d("Valid"), d("Queued"), d("Pt. 1 sent"), d("Pt. 2 sent"), d("Replied YES"), w("Awaiting click")];
    case "completed_no":
      return [d("Imported"), d("Valid"), d("Queued"), d("Pt. 1 sent"), d("Pt. 2 sent"), d("Replied NO"), w("Awaiting feedback")];
    case "opted_out":
      return [d("Imported"), d("Valid"), d("Queued"), d("Pt. 1 sent"), d("Pt. 2 sent"), f("Opted out (STOP)")];
    case "clicked_review":
      return [d("Imported"), d("Valid"), d("Queued"), d("Pt. 1 sent"), d("Pt. 2 sent"), d("Replied YES"), d("Clicked review link")];
    case "clicked_form":
      return [d("Imported"), d("Valid"), d("Queued"), d("Pt. 1 sent"), d("Pt. 2 sent"), d("Replied NO"), d("Clicked feedback form"), w("Awaiting submission")];
    case "form_submitted":
      return [d("Imported"), d("Valid"), d("Queued"), d("Pt. 1 sent"), d("Pt. 2 sent"), d("Replied NO"), d("Clicked feedback form"), d("Form submitted")];
    default:
      return [d("Imported"), w(status)];
  }
}

// effectiveRowStatus prefers the number's live conversation status (attached at
// read time) over the frozen per-row mirror, so every batch shows what is
// actually happening with the number and agrees with the activity log (#36).
function effectiveRowStatus(row: UploadRow): UploadRow["status"] {
  return row.live_status ?? row.status;
}

function renderRowPipeline(status: UploadRow["status"]): string {
  const steps = rowPipelineSteps(status);
  const parts = steps.flatMap((step, i) => {
    const chip = `<span class="pipe-step pipe-${step.state}">${escapeHtml(step.label)}</span>`;
    return i < steps.length - 1 ? [chip, `<span class="pipe-sep">›</span>`] : [chip];
  });
  return `<div class="row-pipeline">${parts.join("")}</div>`;
}

function renderBatchStatusBreakdown(rows: UploadRow[]): string {
  const counts: Partial<Record<UploadRow["status"], number>> = {};
  for (const row of rows) {
    const st = effectiveRowStatus(row);
    counts[st] = (counts[st] ?? 0) + 1;
  }

  const groups: { label: string; statuses: UploadRow["status"][]; cls: string }[] = [
    { label: "Clicked review link", statuses: ["clicked_review"], cls: "bk-done" },
    { label: "Form submitted", statuses: ["form_submitted"], cls: "bk-done" },
    { label: "Clicked feedback form", statuses: ["clicked_form"], cls: "bk-warn" },
    { label: "Awaiting reply", statuses: ["optin_sent"], cls: "bk-warn" },
    { label: "Replied YES — awaiting click", statuses: ["completed_yes"], cls: "bk-warn" },
    { label: "Replied NO — awaiting feedback", statuses: ["completed_no"], cls: "bk-warn" },
    { label: "Queued — ready to send", statuses: ["queued"], cls: "bk-queued" },
    { label: "Opt-in 1 sent (pt. 2 pending)", statuses: ["optin_1_sent"], cls: "bk-warn" },
    { label: "Not yet queued", statuses: ["valid"], cls: "bk-neutral" },
    { label: "Send failed", statuses: ["failed"], cls: "bk-fail" },
    { label: "Opted out (STOP)", statuses: ["opted_out", "skipped_opted_out"], cls: "bk-skip" },
    { label: "Opted out (global)", statuses: ["rejected_global_optout", "skipped_global_optout"], cls: "bk-skip" },
    { label: "Skipped — contacted < 1yr", statuses: ["skipped_recent"], cls: "bk-skip" },
    { label: "Suppressed (prior opt-out)", statuses: ["rejected_suppressed"], cls: "bk-skip" },
    { label: "Invalid number", statuses: ["rejected_invalid", "no_phone"], cls: "bk-fail" },
    { label: "Duplicate", statuses: ["rejected_duplicate"], cls: "bk-fail" },
    { label: "Quota exceeded", statuses: ["rejected_quota"], cls: "bk-fail" },
  ];

  const items = groups
    .map((g) => {
      const n = g.statuses.reduce((sum, s) => sum + (counts[s] ?? 0), 0);
      if (n === 0) return "";
      return `<div class="bk-item ${g.cls}"><span class="bk-count">${n}</span> ${escapeHtml(g.label)}</div>`;
    })
    .filter(Boolean)
    .join("");

  if (!items) return "";
  return `<div class="batch-status-breakdown">${items}</div>`;
}


function renderBanner(state: { notice: string; error: string }) {
  const items = [
    state.error ? `<div class="banner error">${escapeHtml(state.error)}</div>` : "",
    state.notice ? `<div class="banner notice">${escapeHtml(state.notice)}</div>` : "",
  ].filter(Boolean);

  return items.join("");
}

function disabledAttr(disabled: boolean) {
  return disabled ? "disabled" : "";
}

function readError(error: unknown) {
  if (error instanceof Error) {
    return error.message;
  }

  return "An unexpected error occurred.";
}

function formatDate(value: string) {
  try {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function escapeHtml(value: string) {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}
